import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom'; // <--- 1. ADDED ROUTER HOOKS
import { 
  Users, Activity, X, FileText, Zap, Plus, ScanFace, CheckCircle, AlertOctagon, 
  ChevronDown, ChevronRight, Mail, MessageCircle, Send, Paperclip, ShieldAlert, 
  Smartphone, Key, RefreshCw, Shield, Globe, Laptop, Server, TrendingUp, Landmark, 
  Edit2, Save, XCircle, ShieldX, Lock, LogOut, MessageSquare, Clock, UploadCloud, Calendar, CreditCard, Flag, Trash2,
  ShieldCheck, Check, Eye, ExternalLink
} from 'lucide-react';
import { JOURNEY_STAGES, MOCK_CLIENTS_FULL } from '../data/mockData';
import { COMM_TEMPLATES } from '../data/constants';
import { fetchClientDetails, fetchClientsList } from '../services/nommiaApi';
import TicketWorkspace from './TicketWorkspace';
import OverviewTab from './OverviewTab';
import { supabase, fireAutomationEvent } from '../supabaseClient';
import { authFetch } from '../utils/apiClient';

// --- MOCK DB FOR DIRECT LINKING (Fallback Data) ---
const MOCK_CLIENT_DB = {
    '882991': {
        name: 'James Sterling',
        id: '882991',
        tier: 'Elite',
        status: 'Unverified',
        role: 'Trader',
        avatar: 'JS',
        deposit: 45000,
        equity: 42100,
        // ADDED FOR ENHANCED KYC:
        lifetimeDeposit: 45000,
        advancedKycStatus: 'Completed', 
        sumsub: { status: 'Approved', checks: [] },
        // ------------------------
        tickets: [
            { id: 101, subject: 'Withdrawal Pending', priority: 'High', status: 'Open', created: '15m ago', history: [] },
            { id: 992, subject: 'Crypto Deposit Delay', priority: 'High', status: 'Open', created: '2h ago', history: [] }
        ],
        communications: [],
        documents: [],
        tradingAccounts: []
    }
};

const MOCK_COMMS_DATA = [
  {
    id: 'LOG-1001',
    type: 'system',
    direction: 'inbound',
    channel: 'System',
    subject: '[Ticket-402] Deposit Issue Resolved',
    body: 'Action: Status Update\nDetails: Ticket marked as Resolved by Agent.',
    timestamp: '10:42 AM',
    date: 'Today',
    status: 'Closed'
  },
  {
    id: 'LOG-1002',
    type: 'email',
    direction: 'outbound',
    channel: 'Email',
    subject: 'RE: Verification Documents',
    body: 'Dear Client, thank you for uploading your proof of address. We are reviewing it now.',
    timestamp: '09:15 AM',
    date: 'Today',
    status: 'Sent'
  }
];

// =========================================================
// 1. MANUAL UPLOAD MODAL (Production Ready)
// =========================================================
const ManualUploadModal = ({ isOpen, onClose, onUpload }) => {
    const [docCategory, setDocCategory] = React.useState('Proof of ID'); 
    const fileInputRef = React.useRef(null);
    
    // PRODUCTION SETTINGS
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const [isUploading, setIsUploading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const [formData, setFormData] = React.useState({
        idType: 'Passport', poaType: 'Utility Bill', idNumber: '', issuingCountry: '', validFrom: '', expiryDate: '', issueDate: '', 
        file: null 
    });

    // Reset state when opening/closing
    React.useEffect(() => {
        if (isOpen) {
            setError(null);
            setIsUploading(false);
            setFormData({ idType: 'Passport', poaType: 'Utility Bill', idNumber: '', issuingCountry: '', validFrom: '', expiryDate: '', issueDate: '', file: null });
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!formData.file) {
            setError("Please select a file to upload.");
            return;
        }

        setIsUploading(true);
        setError(null);
        
        try {
            // 🚨 THE FIX: Determine the actual document type based on the active tab
            const resolvedType = docCategory === 'Proof of ID' ? formData.idType : formData.poaType;

            // 1. Await the parent component's real Supabase upload
            await onUpload({ ...formData, idType: resolvedType, category: docCategory });
            
            // 2. Close and reset on success
            setIsUploading(false);
            onClose();
        } catch (err) {
            // Catch and display real database/network errors
            setError(err.message || "Upload failed. Please try again.");
            setIsUploading(false);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files && e.target.files[0];
        if (!selectedFile) return;

        // 1. VALIDATION: Check Size
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setError(`File is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
            setFormData({ ...formData, file: null });
            return;
        }

        // 2. VALIDATION: Check Type (Double check client side)
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(selectedFile.type)) {
            setError("Invalid file type. Only PDF, JPG, and PNG are allowed.");
            setFormData({ ...formData, file: null });
            return;
        }

        setError(null);
        setFormData({ ...formData, file: selectedFile });
    };

    return (
        <div className="fixed inset-0 z-[70] bg-slate-950/90 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <UploadCloud size={24} className="text-blue-500" /> Manual KYC Upload
                    </h3>
                    <button onClick={onClose} disabled={isUploading} className="text-slate-400 hover:text-white disabled:opacity-50"><X size={20}/></button>
                </div>

                <div className="flex bg-slate-800 p-1 rounded-lg mb-6">
                    {['Proof of ID', 'Proof of Address'].map(cat => (
                        <button key={cat} onClick={() => setDocCategory(cat)} disabled={isUploading} className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${docCategory === cat ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white disabled:opacity-50'}`}>{cat}</button>
                    ))}
                </div>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                    {/* INPUT FIELDS */}
                    {docCategory === 'Proof of ID' ? (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">ID Type</label><select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none" value={formData.idType} onChange={(e) => setFormData({...formData, idType: e.target.value})}><option>Passport</option><option>National ID</option><option>Driver's License</option></select></div>
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Country</label><div className="relative"><Flag size={14} className="absolute left-3 top-3.5 text-slate-500" /><input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 pl-9 text-white text-sm focus:border-blue-500 outline-none" placeholder="e.g. UK" value={formData.issuingCountry} onChange={(e) => setFormData({...formData, issuingCountry: e.target.value})}/></div></div>
                            </div>
                            <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">ID Number</label><div className="relative"><CreditCard size={14} className="absolute left-3 top-3.5 text-slate-500" /><input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 pl-9 text-white text-sm focus:border-blue-500 outline-none" placeholder="Document Number" value={formData.idNumber} onChange={(e) => setFormData({...formData, idNumber: e.target.value})}/></div></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Valid From</label><input type="date" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none" value={formData.validFrom} onChange={(e) => setFormData({...formData, validFrom: e.target.value})}/></div>
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Expiry Date</label><input type="date" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none" value={formData.expiryDate} onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}/></div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Document Type</label><select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none" value={formData.poaType} onChange={(e) => setFormData({...formData, poaType: e.target.value})}><option>Utility Bill</option><option>Bank Statement</option><option>Tax Letter</option><option>Other</option></select></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Date of Issue</label><div className="relative"><Calendar size={14} className="absolute left-3 top-3.5 text-slate-500" /><input type="date" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 pl-9 text-white text-sm focus:border-blue-500 outline-none" value={formData.issueDate} onChange={(e) => setFormData({...formData, issueDate: e.target.value})}/></div></div>
                                <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Expiry Date</label><input type="date" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none" value={formData.expiryDate} onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}/></div>
                            </div>
                        </>
                    )}

                    {/* LIVE FILE INPUT & DROP AREA */}
                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png" />
                    
                    <div 
                        onClick={() => !isUploading && fileInputRef.current.click()} 
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer relative ${
                            error ? 'border-red-500/50 bg-red-500/10' :
                            formData.file ? 'border-green-500/50 bg-green-500/10' : 
                            'border-slate-700 hover:bg-slate-800/50 hover:border-blue-500/50'
                        }`}
                    >
                        {formData.file ? (
                            <>
                                <CheckCircle size={32} className="mx-auto text-green-500 mb-2 animate-bounce-short" />
                                <p className="text-sm font-bold text-white">{formData.file.name}</p>
                                <p className="text-xs text-green-400">{(formData.file.size / 1024 / 1024).toFixed(2)} MB • Ready to upload</p>
                            </>
                        ) : error ? (
                             <>
                                <ShieldAlert size={32} className="mx-auto text-red-500 mb-2" />
                                <p className="text-sm font-bold text-red-400">{error}</p>
                                <p className="text-xs text-red-300 mt-1">Click to try again</p>
                            </>
                        ) : (
                            <>
                                <UploadCloud size={32} className="mx-auto text-slate-500 mb-2" />
                                <p className="text-sm font-bold text-white">Click to upload file</p>
                                <p className="text-xs text-slate-500">PDF, JPG, PNG (Max {MAX_FILE_SIZE_MB}MB)</p>
                            </>
                        )}
                    </div>
                </div>

                <div className="pt-6 flex gap-3 border-t border-slate-800 mt-4">
                    <button onClick={onClose} disabled={isUploading} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg disabled:opacity-50">Cancel</button>
                    
                    <button 
                        onClick={handleSubmit} 
                        disabled={isUploading || !formData.file || !!error}
                        className={`flex-1 py-3 font-bold rounded-lg flex items-center justify-center gap-2 transition-all ${
                            isUploading 
                            ? 'bg-blue-800 text-blue-200 cursor-wait' 
                            : 'bg-blue-600 hover:bg-blue-500 text-white'
                        }`}
                    >
                        {isUploading ? (
                            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Uploading...</>
                        ) : "Upload & Verify"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 2. DOCUMENT REVIEW MODAL (Upgraded with Dropdown)
// =========================================================
const DocumentReviewModal = ({ isOpen, onClose, doc, onAction }) => {
    const [isRejecting, setIsRejecting] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('Rejected Details Mismatch');

    // Reset state when opened
    React.useEffect(() => { setIsRejecting(false); }, [isOpen]);

    if (!isOpen || !doc) return null;

    return (
        <div className="fixed inset-0 z-[70] bg-slate-950/95 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl h-[80vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="h-16 border-b border-slate-800 flex justify-between items-center px-6 bg-slate-800/50">
                    <div><h3 className="text-lg font-bold text-white flex items-center gap-2"><FileText size={20} className="text-purple-400" /> Reviewing: {doc.type}</h3><p className="text-xs text-slate-400">Source: {doc.source}</p></div>
                    <div className="flex gap-2 items-center">
                        {/* 🔒 MANUAL KYC DECISION CONTROLS HIDDEN (Preserved for automated Didit KYC integration)
                        {isRejecting ? (
                            <div className="flex items-center gap-2 animate-fade-in">
                                <select className="bg-slate-950 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none" value={rejectReason} onChange={e => setRejectReason(e.target.value)}>
                                    <option value="Rejected Details Mismatch">Rejected Details Mismatch</option>
                                    <option value="Rejected Document Quality & Visibility">Rejected Document Quality & Visibility</option>
                                    <option value="Rejected Invalid or Expired Document">Rejected Invalid or Expired Document</option>
                                </select>
                                <button onClick={() => onAction('Rejected', rejectReason)} className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"><CheckCircle size={16} /> Confirm Reject</button>
                                <button onClick={() => setIsRejecting(false)} className="text-slate-400 hover:text-white px-2">Cancel</button>
                            </div>
                        ) : (
                            <>
                                <button onClick={() => onAction('Approved', '')} className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"><CheckCircle size={16} /> Approve</button>
                                <button onClick={() => setIsRejecting(true)} className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"><XCircle size={16} /> Reject</button>
                            </>
                        )}
                        <div className="w-px h-6 bg-slate-700 mx-2"></div>
                        */}
                        <div className="px-2.5 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-medium flex items-center gap-1.5 mr-2">
                            <ShieldCheck size={14} /> Didit Automated KYC Active
                        </div>
                        <button onClick={onClose} className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"><X size={16} /></button>
                    </div>

                </div>
                <div className="flex-1 flex overflow-hidden">
                    <div className="flex-1 bg-black flex items-center justify-center relative overflow-hidden">
                        {doc.fileUrl ? (
                            (doc.fileUrl.toLowerCase().includes('.pdf') || doc.fileUrl.toLowerCase().includes('application/pdf')) ? (
                                <iframe src={doc.fileUrl} className="w-full h-full border-0" title="PDF Preview" />
                            ) : (
                                <img src={doc.fileUrl} alt="Document Preview" className="max-w-full max-h-full object-contain p-4" />
                            )
                        ) : (
                            <span className="text-slate-500">Preview Unavailable (No URL Provided)</span>
                        )}
                    </div>
                    <div className="w-80 bg-slate-900 border-l border-slate-800 p-6 overflow-y-auto">
                        <h4 className="text-xs font-bold text-slate-500 uppercase mb-4 flex items-center gap-2"><Zap size={12} /> AI Extraction</h4>
                        <div className="space-y-4">
                            <div className="bg-slate-800 p-3 rounded-lg border border-slate-700"><label className="text-[10px] text-slate-400 block">Type</label><p className="text-sm font-bold text-white">{doc.type}</p></div>
                            <div className="bg-slate-800 p-3 rounded-lg border border-slate-700"><label className="text-[10px] text-slate-400 block">Expiry</label><p className="text-sm font-bold text-white">{doc.expiry || 'N/A'}</p></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- AUTOMATED DIDIT IDENTITY & BIOMETRIC MEDIA VIEWER MODAL ---
const DiditMediaModal = ({ isOpen, onClose, media, isLoading }) => {
    const [activeTab, setActiveTab] = React.useState('front');

    React.useEffect(() => {
        if (media) {
            if (media.frontImage) setActiveTab('front');
            else if (media.selfieImage) setActiveTab('selfie');
            else if (media.portraitImage) setActiveTab('portrait');
        }
    }, [media]);

    if (!isOpen) return null;

    const availableTabs = [
        { id: 'front', label: 'Front Document', icon: FileText, show: !!media?.frontImage, url: media?.frontImage },
        { id: 'back', label: 'Back Document', icon: FileText, show: !!media?.backImage, url: media?.backImage },
        { id: 'portrait', label: 'Extracted Portrait', icon: ScanFace, show: !!media?.portraitImage, url: media?.portraitImage },
        { id: 'selfie', label: 'Biometric Selfie (3D)', icon: ScanFace, show: !!media?.selfieImage, url: media?.selfieImage },
        { id: 'video', label: 'Liveness Video', icon: Activity, show: !!media?.livenessVideo, url: media?.livenessVideo }
    ].filter(t => t.show);

    const currentMedia = availableTabs.find(t => t.id === activeTab) || availableTabs[0];

    return (
        <div className="fixed inset-0 z-[70] bg-slate-950/95 flex items-center justify-center p-4 animate-fade-in backdrop-blur-md">
            <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-5xl h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="h-16 border-b border-slate-800 flex justify-between items-center px-6 bg-slate-800/60">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400">
                            <ShieldCheck size={20} />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                Verified Client Identity Media (Didit Vault)
                            </h3>
                            <p className="text-xs text-slate-400">
                                {media?.docType || 'Identity Document'} • {media?.issuingState || 'International'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {currentMedia?.url && currentMedia.id !== 'video' && (
                            <a 
                                href={currentMedia.url} 
                                target="_blank" 
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-slate-700"
                            >
                                <ExternalLink size={13} />
                                <span>Open Full Res</span>
                            </a>
                        )}
                        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400">
                        <RefreshCw size={28} className="animate-spin text-blue-400" />
                        <p className="text-sm font-semibold">Retrieving verified ID & biometric media from secure vault...</p>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                        {/* Media Viewer Area */}
                        <div className="flex-1 bg-slate-950 flex flex-col overflow-hidden">
                            {/* Tabs Bar */}
                            <div className="flex border-b border-slate-800 px-4 bg-slate-900/40 gap-2 overflow-x-auto">
                                {availableTabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`px-4 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
                                            activeTab === tab.id 
                                                ? 'border-blue-500 text-blue-400 bg-blue-500/10' 
                                                : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <tab.icon size={14} />
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Main Preview Container */}
                            <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
                                {currentMedia ? (
                                    currentMedia.id === 'video' ? (
                                        <video src={currentMedia.url} controls className="max-w-full max-h-full rounded-xl shadow-lg border border-slate-800" />
                                    ) : (
                                        <img 
                                            src={currentMedia.url} 
                                            alt={currentMedia.label} 
                                            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl border border-slate-800 bg-slate-900/50" 
                                        />
                                    )
                                ) : (
                                    <div className="text-slate-500 text-sm">No image available for this section.</div>
                                )}
                            </div>
                        </div>

                        {/* Extracted Document Information Sidebar */}
                        <div className="w-full md:w-80 bg-slate-900 border-t md:border-t-0 md:border-l border-slate-800 p-5 overflow-y-auto space-y-4">
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <Zap size={13} className="text-blue-400" /> Extracted Document Data
                                </h4>
                                <div className="space-y-2.5">
                                    <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold block">Full Name on ID</span>
                                        <span className="text-sm font-bold text-white">{media?.fullName || 'N/A'}</span>
                                    </div>
                                    <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold block">Document Type</span>
                                        <span className="text-sm font-semibold text-slate-200">{media?.docType || 'Government ID'}</span>
                                    </div>
                                    <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold block">Document Number</span>
                                        <span className="text-sm font-mono font-semibold text-blue-300">{media?.docNumber || 'Protected'}</span>
                                    </div>
                                    <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold block">Issuing Country</span>
                                        <span className="text-sm font-semibold text-slate-200">{media?.issuingState || 'N/A'}</span>
                                    </div>
                                    {media?.dateOfBirth && (
                                        <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                            <span className="text-[10px] text-slate-500 uppercase font-bold block">Date of Birth</span>
                                            <span className="text-sm font-semibold text-slate-200">{media.dateOfBirth}</span>
                                        </div>
                                    )}
                                    {media?.expirationDate && (
                                        <div className="bg-slate-950/70 p-3 rounded-xl border border-slate-800">
                                            <span className="text-[10px] text-slate-500 uppercase font-bold block">Expiration Date</span>
                                            <span className="text-sm font-semibold text-slate-200">{media.expirationDate}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="pt-3 border-t border-slate-800">
                                <div className="text-[11px] text-slate-500">
                                    Encrypted media securely retrieved from compliance vault. Signed links refresh automatically.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================

// --- THE FIX: SMART HTML STRIPPER ---
const stripHtml = (htmlString) => {
    if (!htmlString) return '';
    // 1. Remove CSS <style> and <script> blocks completely
    let text = htmlString.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    // 2. Remove all remaining HTML tags
    text = text.replace(/<[^>]+>/g, ' ');
    // 3. Clean up weird spacing and HTML entities
    text = text.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    return text;
};
// ------------------------------------

const ClientProfile = ({ client: propClient, onClose, userRole, currentUser, onClientSwitch }) => {
  // --- 2. ROUTER INTEGRATION ---
  const { id } = useParams();     // Get ID from URL (e.g., 882991)
  const location = useLocation(); // Get the "State" (activeTab, ticketId)
  const navigate = useNavigate();

// 1. Prioritize the real ID from the URL.
  // THE FIX: Safely convert the ID to a string to completely prevent undefined or Number crashes!
  const safeSearchId = String(id || '');

  const foundClient = MOCK_CLIENTS_FULL.find(c => 
      String(c.id) === safeSearchId || (c.email && c.email.toLowerCase() === safeSearchId.toLowerCase())
  );
  
  const foundMockDb = (id ? MOCK_CLIENT_DB[id] : null) || Object.values(MOCK_CLIENT_DB).find(c => 
      c.email && c.email.toLowerCase() === safeSearchId.toLowerCase()
  );

  const baseClient = propClient || foundClient || foundMockDb || { 
      id: id || 'unknown', 
      name: 'Loading Profile...',
      tickets: [],
      communications: [],
      documents: [],
      tradingAccounts: []
  };

  // 2. LIVE DATA STATE
  const [client, setClient] = useState(baseClient);
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const handleImpersonate = async () => {
      if (!client?.email) {
          return alert("Cannot impersonate: No email on file for this client.");
      }
      setIsImpersonating(true);
      try {
          const response = await authFetch('https://nommia-crm-backend-production.up.railway.app/api/crm/impersonate', {
              method: 'POST',
              body: JSON.stringify({ email: client.email })
          });

          const data = await response.json();
          if (data.success && data.link) {
              // Only fix the typo, do not inject /login since the app routes everything from the root
              const safeLink = data.link.replace('trade.nommia.i', 'trade.nommia.io');
              window.open(safeLink, '_blank');
          } else {
              alert(`Impersonation Failed: ${data.error || 'Unknown error'}`);
          }
      } catch (err) {
          console.error("Impersonation Error:", err);
          alert(`Impersonation Error: ${err.message}`);
      } finally {
          setIsImpersonating(false);
      }
  };

  const [isSyncingDidit, setIsSyncingDidit] = useState(false);
  const handleSyncDidit = async () => {
      const targetUserId = client?.user_id || client?.id;
      if (!targetUserId) return alert("Cannot sync: No user ID found for this client.");
      setIsSyncingDidit(true);
      try {
          const response = await fetch('https://nommia-crm-backend-production.up.railway.app/api/didit/sync', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: targetUserId, sessionId: client?.kyc_session_id })
          });
          const data = await response.json();
          if (data.success) {
              const isApproved = data.kyc_approved || String(data.status).toLowerCase() === 'approved';
              const isDeclined = ['declined', 'rejected'].includes(String(data.status).toLowerCase());

              let xvalleyMessage = '';

              // XVALLEY FALLBACK SYNC:
              // If client is approved, ensure XValley has kycStatus: 'Verified' (enabling KYC and Trading)
              // If declined, leave XValley untouched per compliance instruction
              if (isApproved) {
                  const targetEmail = client.email || (client.id && client.id.includes('@') ? client.id : null) || data.userEmail;
                  if (targetEmail) {
                      try {
                          const xvRes = await authFetch(`${process.env.REACT_APP_XVALLEY_API_URL || 'https://innovative-rebirth-production.up.railway.app'}/api/xvalley/kyc-update`, {
                              method: 'POST',
                              body: JSON.stringify({
                                  email: targetEmail,
                                  kycStatus: 'Verified'
                              })
                          });
                          const xvJson = await xvRes.json().catch(() => ({}));
                          if (xvRes.ok && xvJson.success) {
                              xvalleyMessage = '\n• XValley: KYC Approved & Trading Enabled';
                          } else if (xvJson.error === 'User not found in XValley') {
                              xvalleyMessage = '\n• XValley: Account not created yet in XValley (Sync will apply once created)';
                          } else {
                              xvalleyMessage = `\n• XValley Sync: ${xvJson.error || 'Pending'}`;
                          }
                      } catch (xvErr) {
                          console.warn("Direct XValley sync fallback warning:", xvErr);
                          if (data.xvalleySynced) {
                              xvalleyMessage = '\n• XValley: Synced via Backend Bridge';
                          }
                      }
                  } else if (data.xvalleySynced) {
                      xvalleyMessage = '\n• XValley: Synced via Backend Bridge';
                  }

                  // Lift local trading restrictions in state
                  setIsTradingRestricted(false);
              } else if (isDeclined) {
                  xvalleyMessage = '\n• XValley: Untouched (Compliance decline)';
              }

              setClient(prev => ({
                  ...prev,
                  kyc_status: data.status,
                  kyc_approved: isApproved,
                  kyc_session_id: data.sessionId || prev.kyc_session_id,
                  kyc_document_type: data.docType || prev.kyc_document_type,
                  kyc_country: data.country || prev.kyc_country,
                  aml_hits: data.amlHits !== undefined ? data.amlHits : prev.aml_hits,
                  aml_screened_at: data.amlScreenedAt || prev.aml_screened_at,
                  kyc_human_reviewed: data.hasHumanReview !== undefined ? data.hasHumanReview : prev.kyc_human_reviewed,
                  kyc_rejection_reason: data.declineReason || prev.kyc_rejection_reason,
                  kyc_completed_at: data.completedAt || prev.kyc_completed_at,
                  kyc_updated_at: new Date().toISOString(),
                  kycStatus: isApproved ? 'Approved' : (isDeclined ? 'Declined' : 'Pending Review')
              }));
              alert(`Didit Status Synced: ${data.status} (Approved: ${isApproved})${xvalleyMessage}`);
          } else {
              alert(`Didit Sync: ${data.reason || 'No decision updates found'}`);
          }
      } catch (err) {
          console.error("Didit Sync Error:", err);
          alert(`Didit Sync Error: ${err.message}`);
      } finally {
          setIsSyncingDidit(false);
      }
  };

  const [diditMedia, setDiditMedia] = useState(null);
  const [isLoadingDiditMedia, setIsLoadingDiditMedia] = useState(false);
  const [showDiditMediaModal, setShowDiditMediaModal] = useState(false);

  const handleOpenDiditMedia = async () => {
      const identifier = client?.kyc_session_id || client?.user_id || client?.id || client?.email;
      if (!identifier) {
          return alert("No verification session found for this client.");
      }
      setIsLoadingDiditMedia(true);
      setShowDiditMediaModal(true);
      try {
          const res = await fetch(`https://nommia-crm-backend-production.up.railway.app/api/didit/media/${encodeURIComponent(identifier)}`);
          const data = await res.json();
          if (data.success) {
              setDiditMedia(data);
          } else {
              alert(`Could not load ID images: ${data.error || 'No media found'}`);
              setShowDiditMediaModal(false);
          }
      } catch (err) {
          console.error("Failed to fetch Didit media:", err);
          alert(`Error loading ID images: ${err.message}`);
          setShowDiditMediaModal(false);
      } finally {
          setIsLoadingDiditMedia(false);
      }
  };

  // --- AUTOMATED & AGENT-TRIGGERED EDD DIDIT SESSIONS ---
  const [isTriggeringEdd, setIsTriggeringEdd] = useState(false);
  const handleTriggerEddSession = async (type = 'POA', sendEmail = false) => {
      const vendorId = client?.user_id || client?.id || client?.email;
      const targetEmail = client?.email || (client?.id && client?.id.includes('@') ? client?.id : null);
      if (!vendorId) {
          return alert("Cannot trigger EDD: Missing client identifier.");
      }
      setIsTriggeringEdd(true);
      try {
          const res = await fetch('https://nommia-crm-backend-production.up.railway.app/api/didit/session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  vendorData: vendorId,
                  workflowType: type,
                  email: targetEmail,
                  sendEmail: sendEmail
              })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to create EDD session');

          if (sendEmail) {
              alert(`✅ Automated ${type === 'POA' ? 'Proof of Address (Level 1)' : 'Source of Funds (Level 2)'} invitation email successfully dispatched to ${targetEmail || 'client'}!`);
          } else if (data.url) {
              window.open(data.url, '_blank');
          }
      } catch (err) {
          console.error("EDD session error:", err);
          alert(`EDD Error: ${err.message}`);
      } finally {
          setIsTriggeringEdd(false);
      }
  };

  const clientRef = useRef(client);

  // Keep the ref constantly updated with the latest client data
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  // 3. FETCH LIVE DETAILS FROM SUPABASE MASTER TABLE
  useEffect(() => {
      const loadLiveDetails = async () => {
          if (!baseClient || !baseClient.id) return;
          setIsLiveLoading(true);
          
          let liveData = {};
          let xvalley = null;
          let powerAutomateTickets = []; 
          let liveSenderName = null;  
          let liveSenderEmail = null; 
          let xvalleyLedgers = {};

          // ==========================================
          // 1. FETCH FROM SUPABASE MASTER TABLE (user_broker_accounts)
          // ==========================================
          let searchParam = String(baseClient.id || '');
          let isEmailSearch = searchParam.includes('@');

          try {
              console.log(`📡 Fetching master profile from Supabase for: ${searchParam}`);
              
              let query = supabase.from('user_broker_accounts').select('*');
              
              if (isEmailSearch) {
                  // Email Search: Check real email column and legacy user_id
                  query = query.or(`email.eq.${searchParam},user_id.eq.${searchParam}`);
              } else {
                  // 🛡️ THE SAFETY NET: Check the new UUID first, but fall back to legacy IDs if clicking an old ticket
                  query = query.or(`user_id.eq.${searchParam},account_id.eq.${searchParam},id.eq.${searchParam}`);
              }

              // 🚨 Bulletproof duplicate handling
              const { data: brokerRows, error: brokerError } = await query
                  .order('created_at', { ascending: false })
                  .limit(5); // 👈 CHANGE TO 5: Fetch a small pool to bypass ghost rows

              if (brokerError) throw brokerError;

              if (brokerRows && brokerRows.length > 0) {
                  // 🚨 THE FIX: Ignore ghost rows! Find the row that actually has an email and name.
                  const brokerData = brokerRows.find(r => r.email && r.name && !r.email.includes('-')) || brokerRows[0];
                  
                  console.log("✅ Supabase Master Profile Found:", brokerData);
                  
                  let realReferrerName = brokerData.referred_by || 'Direct Sign-up';
                  if (brokerData.referred_by) {
                      const { data: refData } = await supabase
                          .from('user_broker_accounts')
                          .select('name')
                          .eq('referral_code', brokerData.referred_by)
                          .maybeSingle();
                          
                      if (refData && refData.name) {
                          realReferrerName = refData.name;
                      }
                  }

                  liveData = {
                      id: brokerData.account_id || brokerData.id,
                      email: brokerData.email || brokerData.user_id,
                      registeredDate: brokerData.created_at,
                      firstName: brokerData.first_name,
                      lastName: brokerData.last_name,
                      name: brokerData.name,
                      phoneNumber: brokerData.phone || '',
                      country: brokerData.country_residence,
                      address: {
                          street: brokerData.street || '',
                          streetNumber: brokerData.street_number || '',
                          city: brokerData.city || '',
                          postalCode: brokerData.post_code || '',
                          country: brokerData.country_residence || 'Unknown'
                      },
                      dateOfBirth: brokerData.birth_date,
                      nationality: brokerData.nationality,
                      occupation: brokerData.employment_status,
                      sourceOfFunds: brokerData.source_of_funds,
                      annualVolume: brokerData.annual_volume,
                      kycStatus: (brokerData.kyc_approved || String(brokerData.kyc_status).toLowerCase() === 'approved') ? 'VERIFIED' : 'PENDING',
                      kyc: { status: (brokerData.kyc_approved || String(brokerData.kyc_status).toLowerCase() === 'approved') ? 'VERIFIED' : 'PENDING' },
                      referral: {
                          referralCode: brokerData.referral_code,
                          referredBy: { name: realReferrerName }
                      },
                      user_id: brokerData.user_id,
                      kyc_status: brokerData.kyc_status,
                      kyc_approved: brokerData.kyc_approved,
                      kyc_session_id: brokerData.kyc_session_id,
                      kyc_document_type: brokerData.kyc_document_type || brokerData.kyc_id_type,
                      kyc_country: brokerData.kyc_country || brokerData.country_residence,
                      kyc_id_type: brokerData.kyc_id_type,
                      kyc_id_reference: brokerData.kyc_id_reference,
                      kyc_id_expiry: brokerData.kyc_id_expiry,
                      kyc_address_type: brokerData.kyc_address_type,
                      kyc_address_date: brokerData.kyc_address_date,
                      aml_hits: brokerData.aml_hits,
                      aml_screened_at: brokerData.aml_screened_at,
                      kyc_human_reviewed: brokerData.kyc_human_reviewed,
                      kyc_rejection_reason: brokerData.kyc_rejection_reason,
                      kyc_completed_at: brokerData.kyc_completed_at,
                      kyc_updated_at: brokerData.kyc_updated_at
                  };
              } else {
                  console.warn(`❌ Could not find ${searchParam} in Supabase user_broker_accounts. Engaging Profile Fallback...`);
                  
                  // 🚨 THE FIX: AUTO-HEALING FALLBACK
                  // If they slipped through the migration, rescue their data from the 'profiles' table!
                  let fallbackQuery = supabase.from('profiles').select('*');
                  
                  if (isEmailSearch) {
                      fallbackQuery = fallbackQuery.eq('email', searchParam);
                  } else {
                      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchParam);
                      if (isUUID) {
                          fallbackQuery = fallbackQuery.or(`user_id.eq.${searchParam},id.eq.${searchParam}`);
                      } else {
                          // If it's not an email and not a UUID, we can't query UUID columns with it.
                          // Fallback to searching by full name or just returning nothing.
                          fallbackQuery = fallbackQuery.eq('user_id', '00000000-0000-0000-0000-000000000000'); // Dummy to return 0 rows
                      }
                  }
                  
                  const { data: profileRows } = await fallbackQuery.limit(1);
                  
                  if (profileRows && profileRows.length > 0) {
                      const fallbackData = profileRows[0];
                      console.log("⚠️ Rescued Fallback Profile from 'profiles' table:", fallbackData);
                      
                      liveData = {
                          id: fallbackData.user_id || fallbackData.id,
                          user_id: fallbackData.user_id || fallbackData.id,
                          email: fallbackData.email,
                          is_missing_broker_row: true, 
                          registeredDate: fallbackData.updated_at || new Date().toISOString(),
                          firstName: fallbackData.full_name?.split(' ')[0] || '',
                          lastName: fallbackData.full_name?.split(' ').slice(1).join(' ') || '',
                          name: fallbackData.full_name || fallbackData.display_name || fallbackData.email,
                          phoneNumber: 'N/A', 
                          country: 'Unknown',
                          kycStatus: fallbackData.kyc_status === 'approved' ? 'VERIFIED' : 'PENDING',
                          kyc: { status: fallbackData.kyc_status === 'approved' ? 'VERIFIED' : 'PENDING' },
                          kyc_status: fallbackData.kyc_status,
                          kyc_approved: fallbackData.kyc_status === 'approved',
                          referral: { referralCode: 'N/A', referredBy: { name: 'Direct Sign-up' } }
                      };
                  }
              }
          } catch (e) {
              console.error("Supabase Master fetch failed:", e);
          }

          // 🚨 Guaranteed email for the rest of the fetches
          const guaranteedEmail = liveData.email || baseClient.email || (isEmailSearch ? searchParam : null);

          // ==========================================
          // 1.B FETCH TRADING TOP-UP ADDRESSES FROM PROFILES
          // ==========================================
          let topupAddresses = { evm: null, btc: null, tron: null };
          if (guaranteedEmail || searchParam) {
              try {
                  let topupQuery = supabase.from('profiles').select('eth_deposit_address, btc_deposit_address, tron_deposit_address');
                  
                  // Use the exact same query logic as the Master Profile to prevent hitting ghost/duplicate rows
                  if (isEmailSearch) {
                      topupQuery = topupQuery.eq('email', searchParam);
                  } else {
                      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchParam);
                      if (isUUID) {
                          topupQuery = topupQuery.or(`user_id.eq.${searchParam},id.eq.${searchParam}`);
                      } else {
                          topupQuery = topupQuery.eq('user_id', '00000000-0000-0000-0000-000000000000');
                      }
                  }
                  
                  const { data: topupRows } = await topupQuery.limit(1);
                  if (topupRows && topupRows.length > 0) {
                      console.log("🔍 Top-Up Raw Row Data:", topupRows[0]); // Logging the actual row to see what Supabase returned
                      topupAddresses = {
                          evm: topupRows[0].eth_deposit_address || null,
                          btc: topupRows[0].btc_deposit_address || null,
                          tron: topupRows[0].tron_deposit_address || null
                      };
                      console.log("✅ Top-Up Addresses Found:", topupAddresses);
                  } else {
                      console.log("⚠️ No Top-Up Addresses found in profiles table for this user.");
                  }
              } catch (e) {
                  console.error("Supabase TopUp Address fetch failed:", e);
              }
          }

          // ==========================================
          // 2. FETCH XVALLEY ACCOUNTS (FAST LOAD)
          // ==========================================
          if (guaranteedEmail) {
              try {
                  console.log(`\n📡 Fetching XValley Accounts for '${guaranteedEmail}'...`);
                  const xvalleyRes = await fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/account/${encodeURIComponent(guaranteedEmail)}`);
                  
                  if (xvalleyRes.ok) {
                      const xvalleyData = await xvalleyRes.json();
                      if (xvalleyData && xvalleyData.accountId) {
                          xvalley = xvalleyData;
                          console.log(`🟢 Accounts Received: ${xvalleyData.allAccounts?.length || 0}`);
                      }
                  } else {
                      console.warn(`⚠️ XValley Accounts Bridge sleeping/disconnected.`);
                  }
              } catch (err) {
                  console.error("🔴 Failed to connect to XValley Bridge.", err);
              }
          }

          // ==========================================
          // 2B. FETCH CROSSMINT CRYPTO WALLET
          // ==========================================
          let cryptoData = { address: null, balances: [], transactions: [] };
          const crossmintId = liveData.user_id || client.user_id || client.id || null;
          
          if (crossmintId) {
              try {
                  console.log(`\n📡 Fetching Crossmint Wallet for user ID '${crossmintId}'...`);
                  const cryptoRes = await authFetch(`https://nommia-crm-backend-production.up.railway.app/api/crm/clients/${encodeURIComponent(crossmintId)}/wallet`);
                  
                  if (cryptoRes.ok) {
                      cryptoData = await cryptoRes.json();
                      console.log(`🟢 Crypto Wallet Received: ${cryptoData.address || 'None'}`);
                  } else {
                      console.warn(`⚠️ Crypto Wallet fetch failed or returned empty.`);
                  }
              } catch (err) {
                  console.error("🔴 Failed to fetch Crossmint Wallet.", err);
              }
          }

          // ==========================================
          // 2D. FETCH SECURITY LOGS (Live Auth Events)
          // ==========================================
          let liveSecurityEvents = [];
          try {
              if (guaranteedEmail) {
                  const { data: secLogs, error: secError } = await supabase
                      .from('crm_security_logs') 
                      .select('*')
                      .eq('client_email', guaranteedEmail) 
                      .order('created_at', { ascending: false })
                      .limit(5); 
                  
                  if (!secError && secLogs && secLogs.length > 0) {
                      liveSecurityEvents = secLogs.map(log => ({
                          id: log.id,
                          location: log.location || 'Unknown',
                          ip: log.ip_address || '0.0.0.0',
                          type: log.event_type || 'Login',
                          risk: log.risk_level || 'Low',
                          time: new Date(log.created_at).toLocaleString()
                      }));
                  }
              }
          } catch (err) {
              console.error("🔴 Failed to fetch Security Logs.", err);
          }

          // ==========================================
          // 3. FETCH LIVE KYC DATA (Supabase CRM Documents)
          // ==========================================
          // We no longer fetch from Nommia API! We solely rely on our CRM Documents table.
          let manualCrmDocs = [];
          try {
              console.log(`📡 Searching Supabase CRM for Manual Docs...`);
              const searchId = liveData.id || client.id || baseClient.id;
              
              if(guaranteedEmail || searchId) {
                  let query = supabase.from('crm_documents').select('*').order('created_at', { ascending: false });
                  
                  if (guaranteedEmail && searchId) {
                       query = query.or(`client_email.eq.${guaranteedEmail},client_id.eq.${searchId}`);
                  } else if (guaranteedEmail) {
                       query = query.eq('client_email', guaranteedEmail);
                  } else {
                       query = query.eq('client_id', searchId);
                  }

                  const { data: dbDocs } = await query;

                  if (dbDocs) {
                      manualCrmDocs = dbDocs.map(doc => ({
                          id: doc.file_path, 
                          type: doc.document_type,
                          status: doc.status,
                          source: doc.source || 'Admin Upload',
                          expiry: doc.expiry_date || 'N/A',
                          fileUrl: null 
                      }));
                  }
              }
          } catch (err) {
              console.error("Failed to fetch manual CRM docs:", err);
          }

          if (manualCrmDocs.length > 0) {
              setDocuments(manualCrmDocs); // Just overwrite directly, it's the source of truth
          }

          // ==========================================
          // 3. BULLETPROOF SUPABASE TICKET FETCH
          // ==========================================
          try {
              const targetTicketId = location.state?.ticketId;
              let fetchedTickets = [];

              if (targetTicketId) {
                  const { data: exactTicket, error: exactError } = await supabase
                      .from('crm_tickets')
                      .select('*')
                      .eq('id', targetTicketId);
                  
                  if (!exactError && exactTicket && exactTicket.length > 0) {
                      fetchedTickets = [...exactTicket];
                  }
              }

              // 🚨 THE FIX: Fetch by Email OR Client UUID
              const searchId = liveData.id || client.id || baseClient.id;

              if (guaranteedEmail || searchId) {
                  let ticketQuery = supabase.from('crm_tickets').select('*').order('created_at', { ascending: false });
                  
                  if (guaranteedEmail && searchId) {
                      ticketQuery = ticketQuery.or(`client_email.eq.${guaranteedEmail},client_id.eq.${searchId}`);
                  } else if (guaranteedEmail) {
                      ticketQuery = ticketQuery.eq('client_email', guaranteedEmail);
                  } else if (searchId) {
                      ticketQuery = ticketQuery.eq('client_id', searchId);
                  }

                  const { data: matchedTickets, error: ticketError } = await ticketQuery;
                      
                  if (!ticketError && matchedTickets) {
                      const existingIds = new Set(fetchedTickets.map(t => t.id));
                      matchedTickets.forEach(t => {
                          if (!existingIds.has(t.id)) fetchedTickets.push(t);
                      });
                  }
              }

              const crmTickets = fetchedTickets;
              
              if (crmTickets.length > 0) {
                  liveSenderName = crmTickets[0].client_name || crmTickets[0].client_email || 'Unknown Sender';
                  liveSenderEmail = crmTickets[0].client_email;

                  powerAutomateTickets = crmTickets.map(ticket => ({
                      id: ticket.id.substring(0, 8), 
                      fullId: ticket.id, 
                      live_chat_id: ticket.live_chat_id || null, 
                      client_id: ticket.client_id || null,       
                      subject: ticket.subject || 'No Subject',
                      priority: ticket.priority || 'Medium',
                      status: ticket.status || 'New',
                      assignedTo: ticket.assigned_to,
                      created: new Date(ticket.created_at).toLocaleDateString(),
                      category: ticket.category,
                      description: ticket.description,
                      sentiment: ticket.sentiment || 'Neutral', 
                      intent: ticket.intent || 'General',        
                      drafts: ticket.drafts || [],
                      history: [
                          {
                              id: `desc-${ticket.id}`,
                              type: 'system',
                              sender: 'client',
                              text: ticket.description || 'No description provided.',
                              timestamp: new Date(ticket.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          },
                          ...(ticket.history || []).filter(h => h.id !== 'original-desc' && !String(h.id).startsWith('desc-'))
                      ]
                  }));
              }
          } catch (err) {
              console.error("Failed to fetch tickets from Supabase:", err);
          }

          // ==========================================
          // 3.B FETCH STANDALONE COMMUNICATIONS & AUDIT LOGS
          // ==========================================
          let standaloneComms = [];
          let sysLogs = [];
          
          if (guaranteedEmail) {
              try {
                  const { data: commsData, error: commsError } = await supabase
                      .from('crm_communications')
                      .select('*')
                      .eq('client_email', guaranteedEmail)
                      .order('created_at', { ascending: false });

                  if (!commsError && commsData) {
                      standaloneComms = commsData.map(comm => ({
                          id: comm.id,
                          type: comm.type,
                          subject: comm.subject || 'Outbound Message',
                          date: new Date(comm.created_at).toLocaleDateString(),
                          status: comm.status || 'Sent',
                          messages: comm.history ? comm.history.map(h => ({
                              sender: h.sender, text: h.text, time: new Date(h.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          })) : [{ sender: comm.agent_name || 'Staff', text: comm.body || 'No content', time: new Date(comm.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }]
                      }));
                  }

                  const { data: logsData } = await supabase
                      .from('crm_system_logs')
                      .select('*')
                      .eq('client_email', guaranteedEmail)
                      .order('created_at', { ascending: false });

                  if (logsData) {
                      sysLogs = logsData.map(log => ({
                          id: log.id,
                          action: log.action,
                          desc: log.description, 
                          actor: log.actor,
                          type: log.type,
                          time: new Date(log.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      }));
                  }
              } catch (err) {
                  console.error("Failed to fetch comms or logs:", err);
              }
          }

          // ==========================================
          // 4. MERGE EVERYTHING INTO UI 
          // ==========================================
          const isBackendVerified = liveData.kycStatus === 'VERIFIED'; 
          
          let liveStatus = 'registered'; 
          if (isBackendVerified) {
              // Since we don't have fiat balances natively in user_broker_accounts yet, 
              // we base 'active' purely on if xvalley accounts exist and have >0 balance
              let hasFunded = false;
              if (xvalley && xvalley.allAccounts) {
                   hasFunded = xvalley.allAccounts.some(acc => acc.isLive && parseFloat(acc.balance) > 0);
              }
              liveStatus = hasFunded ? 'active' : 'kyc_approved';
          } else {
              // 🚨 Check automated Didit status & manual documents
              const isDeclined = String(liveData.kyc_status).toLowerCase() === 'declined' || String(liveData.kyc_status).toLowerCase() === 'rejected';
              const rejectedDocs = manualCrmDocs.filter(doc => doc.status === 'Rejected');
              const activeDocs = manualCrmDocs.filter(doc => doc.status !== 'Rejected');
              
              if (isDeclined || rejectedDocs.length > 0) {
                  liveStatus = 'kyc_rejected';
              } else if (activeDocs.length >= 2 || ['pending', 'in_review', 'in_progress'].includes(String(liveData.kyc_status).toLowerCase())) {
                  liveStatus = 'kyc_pending';
              } else {
                  liveStatus = 'registered'; 
              }
          }

          setJourneyStage(liveStatus);

          setClient(prev => {
              return {
                  ...prev, 
                  id: liveData.id || prev.id,
                  user_id: liveData.user_id || liveData.id || prev.user_id || prev.id,
                  name: liveData.name || liveSenderName || prev.name || 'Guest Sender',
                  email: guaranteedEmail,
                  is_missing_broker_row: liveData.is_missing_broker_row,
                  avatar: (liveData.firstName ? liveData.firstName.charAt(0) : (liveSenderName ? liveSenderName.charAt(0) : '?')).toUpperCase(),
                  firstName: liveData.firstName || '-',
                  lastName: liveData.lastName || '-',
                  
                  country: liveData.address?.country || 'Unknown',
                  phone: liveData.phoneNumber || 'N/A',
                  
                  referralCode: liveData.referral?.referralCode || 'N/A',
                  referrer: liveData.referral?.referredBy?.name || 'Direct Sign-up',
                  
                  dob: liveData.dateOfBirth || 'N/A',
                  dateOfBirth: liveData.dateOfBirth || 'N/A',
                  nationality: liveData.nationality || '-',

                  registeredDate: liveData.registeredDate 
                      ? new Date(liveData.registeredDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) 
                      : (prev.registeredDate || 'Unknown'),
                  
                  // DIDIT & VERIFICATION DATA
                  kyc_status: liveData.kyc_status !== undefined ? liveData.kyc_status : prev.kyc_status,
                  kyc_approved: liveData.kyc_approved !== undefined ? liveData.kyc_approved : prev.kyc_approved,
                  kyc_session_id: liveData.kyc_session_id || prev.kyc_session_id,
                  kyc_document_type: liveData.kyc_document_type || prev.kyc_document_type,
                  kyc_country: liveData.kyc_country || prev.kyc_country,
                  aml_hits: liveData.aml_hits !== undefined ? liveData.aml_hits : prev.aml_hits,
                  aml_screened_at: liveData.aml_screened_at || prev.aml_screened_at,
                  kyc_human_reviewed: liveData.kyc_human_reviewed !== undefined ? liveData.kyc_human_reviewed : prev.kyc_human_reviewed,
                  kyc_rejection_reason: liveData.kyc_rejection_reason || prev.kyc_rejection_reason,
                  kyc_completed_at: liveData.kyc_completed_at || prev.kyc_completed_at,
                  kyc_updated_at: liveData.kyc_updated_at || prev.kyc_updated_at,

                  xvalley: xvalley,
                  xvalleyLedgers: xvalleyLedgers,
                  crypto: cryptoData,
                  topupAddresses: topupAddresses,
                  rawTrades: [],
                  rawTransactions: [],
                  
                  securityEvents: liveSecurityEvents.length > 0 ? liveSecurityEvents : prev.securityEvents,
                  
                  tradingAccounts: (xvalley && xvalley.allAccounts && xvalley.allAccounts.length > 0)
                      ? xvalley.allAccounts.map(acc => ({
                          id: acc.accountId,
                          parentId: acc.parentId || acc.ParentId || null,
                          platform: 'XValley',
                          name: acc.name || 'Standard Account',
                          type: acc.name || 'Standard Account',
                          isLive: !acc.isDemo,
                          isDemo: Boolean(acc.isDemo),
                          is10x: Boolean(acc.is10x),
                          isFunded: Boolean(acc.isFunded),
                          group: acc.group || acc.Group || '',
                          leverage: '1:100', 
                          balance: acc.balance,
                          equity: acc.equity,
                          marginUsed: acc.marginUsed
                      })) 
                      : prev.tradingAccounts,

                  status: liveStatus,
                  journeyStage: liveStatus,

                  kycStatus: isBackendVerified ? 'Approved' : 
                             (liveStatus === 'kyc_rejected' || String(liveData.kyc_status).toLowerCase() === 'declined' || String(liveData.kyc_status).toLowerCase() === 'rejected') ? 'Declined' :
                             liveStatus === 'kyc_pending' ? 'Pending Review' : 'Not Started',
                  
                  advancedKycStatus: 'Required',
                  
                  address: liveData.address,
                  occupation: liveData.occupation || '-'
              };
          });

          // --- UPDATE TICKETS UI ---
          if (powerAutomateTickets.length > 0) {
              setTickets(powerAutomateTickets);
          }

          // --- FORMAT TICKETS FOR THE INBOX ---
          const liveTicketComms = powerAutomateTickets.map(tkt => ({
              id: tkt.id,
              type: 'ticket',
              subject: `[Ticket] ${tkt.subject}`,
              date: tkt.created,
              status: tkt.status,
              messages: tkt.history 
          }));

          // --- MERGE BOTH STANDALONE COMMS AND TICKETS ---
          setCommunications(prev => {
              const existingIds = new Set(prev.map(c => String(c.id)));
              const newTicketComms = liveTicketComms.filter(c => !existingIds.has(String(c.id)));
              const newStandaloneComms = standaloneComms.filter(c => !existingIds.has(String(c.id)));
              return [...newStandaloneComms, ...newTicketComms, ...prev];
          });

          // 🚨 Inject the System Logs into the UI
          if (sysLogs.length > 0) {
              setSystemNotifications(sysLogs);
          }

          setIsLiveLoading(false);
      };

      loadLiveDetails();
  }, [baseClient?.id, location.state?.ticketId]);

  // -----------------------------

  const [activeTab, setActiveTab] = useState('overview');
  
  // --- EMAIL UPDATE MODAL STATE ---
  const [showEmailUpdateModal, setShowEmailUpdateModal] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const handleEmailUpdateSubmit = async () => {
      if (!newEmailInput || !newEmailInput.includes('@')) {
          alert('Please enter a valid email address.');
          return;
      }
      
      const targetEmail = client.email || baseClient?.email;
      if (!targetEmail) {
          alert('Cannot update: Current client email is missing.');
          return;
      }

      if (!window.confirm(`Are you sure you want to change this client\'s primary email from ${targetEmail} to ${newEmailInput}? This will update Supabase Auth, CRM Ledgers, and XValley.`)) {
          return;
      }

      setIsUpdatingEmail(true);
      try {
          const response = await authFetch(`${process.env.REACT_APP_XVALLEY_API_URL || 'https://xvalley-api-production.up.railway.app'}/api/admin/change-email`, {
              method: 'POST',
              body: JSON.stringify({
                  oldEmail: targetEmail,
                  newEmail: newEmailInput.trim()
              })
          });

          const result = await response.json();
          if (result.success) {
              alert('Email updated successfully! Please close this profile and reopen it to see the changes.');
              setShowEmailUpdateModal(false);
              setNewEmailInput('');
              // If the parent provided an onClientSwitch/onClose, we could call it here, but an alert is enough for now.
          } else {
              alert(`Error: ${result.error}`);
          }
      } catch (err) {
          console.error(err);
          alert('Failed to connect to the backend server.');
      } finally {
          setIsUpdatingEmail(false);
      }
  };


  // --- CLIENT DISPUTES STATE & FETCH ---
  const [clientDisputes, setClientDisputes] = useState([]);
  const [isDisputesLoading, setIsDisputesLoading] = useState(false);

  useEffect(() => {
      const fetchClientDisputes = async () => {
          if (activeTab !== 'disputes') return; // Only fetch if they click the tab
          
          const targetId = client.id || baseClient?.id;
          if (!targetId || targetId === 'unknown') return;
          
          setIsDisputesLoading(true);
          try {
              const { data, error } = await supabase
                  .from('reported_transactions')
                  .select('*')
                  .eq('user_id', targetId)
                  .order('created_at', { ascending: false });

              if (!error && data) {
                  setClientDisputes(data);
              }
          } catch (err) {
              console.error("Failed to fetch client disputes:", err);
          } finally {
              setIsDisputesLoading(false);
          }
      };
      fetchClientDisputes();
  }, [client.id, baseClient?.id, activeTab]);

  // --- ADD SECURITY STATE ---
  const [securityEvents, setSecurityEvents] = useState(client.securityEvents || []);
  const [riskScore, setRiskScore] = useState(client.securityRiskScore || 0);

  // --- SAFETY CHECK: PREVENT CRASHES ON MISSING DATA ---
  const safeTradingAccounts = client.tradingAccounts && client.tradingAccounts.length > 0 
      ? client.tradingAccounts 
      : [{ 
          id: 'N/A', 
          platform: 'N/A', 
          type: 'Standard', 
          leverage: '1:100', 
          balance: client.deposit || 0, 
          equity: client.equity || 0
        }];

  const mainAccount = safeTradingAccounts[0];
  // -----------------------------------------------------

  // 🚨 THE FIX: Decouple from mockData. Define production stages directly in the live component.
  const LIVE_JOURNEY_STAGES = [
      { id: 'registered', label: 'Registered', color: 'slate', icon: 'User' },
      { id: 'kyc_pending', label: 'KYC Pending', color: 'amber', icon: 'Clock' },
      { id: 'kyc_rejected', label: 'KYC Rejected', color: 'red', icon: 'ShieldAlert', description: 'Documents rejected. Waiting on client.' },
      { id: 'kyc_approved', label: 'KYC Approved', color: 'blue', icon: 'ShieldCheck' },
      { id: 'active', label: 'Active Trader', color: 'emerald', icon: 'TrendingUp' }
  ];

  const [journeyStage, setJourneyStage] = useState(client.status);
  const currentStageIndex = Math.max(0, LIVE_JOURNEY_STAGES.findIndex(s => s.id === journeyStage));
  
  const [tickets, setTickets] = useState(client.tickets || []); 
  const [selectedTicket, setSelectedTicket] = useState(null);   
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [commsHistory, setCommsHistory] = useState(MOCK_COMMS_DATA);

  const [showNewCommModal, setShowNewCommModal] = useState(false);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicketData, setNewTicketData] = useState({ subject: '', category: 'General', priority: 'Medium', notes: '' });
  const [newCommData, setNewCommData] = useState({ type: 'email', template: 'custom', subject: '', message: '', useCustomHtml: false });
  // --- NEW: LIVE TEMPLATES & PREVIEW STATE ---
  const [liveTemplates, setLiveTemplates] = useState({ email: [], whatsapp: [] });
  const [masterHtml, setMasterHtml] = useState('');
  const [showCommPreview, setShowCommPreview] = useState(false);

  // Fetch Live Templates from Supabase
      useEffect(() => {
          const fetchTemplates = async () => {
              try {
                  const { data, error } = await supabase.from('crm_templates').select('*');
                  if (!error && data) {
                      const master = data.find(t => t.id === 'master_html');
                      if (master) setMasterHtml(master.body);

                      // THE FIX: The strict wall that blocks any template flagged as is_system
                      setLiveTemplates({
                          email: data.filter(t => t.type === 'email' && !t.is_system),
                          whatsapp: data.filter(t => t.type === 'whatsapp' && !t.is_system)
                      });
                  }
              } catch (e) {
                  console.error("Failed to load live templates", e);
              }
          };
          fetchTemplates();
      }, []);
  // -------------------------------------------

  const [communications, setCommunications] = useState(client.communications || []);
  const [systemNotifications, setSystemNotifications] = useState(client.systemNotifications || []);
  const [bioRecoveryStage, setBioRecoveryStage] = useState(client.bioRecoveryStatus === 'approved' ? 'approved' : 'idle');

  const [selectedCommId, setSelectedCommId] = useState(null);

  const isSumsubTier = ['Connect', 'Pro', 'Elite'].includes(client.tier);

  // --- UNIVERSAL AUDIT LOGGER ---
  const logSystemActivity = async (action, desc, type = 'system') => {
      // 1. Update the UI instantly
      const newLog = {
          id: `log-${Date.now()}`,
          action: action,
          desc: desc, // Keep 'desc' for the UI state mapping
          actor: userRole || 'Agent',
          time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
          type: type
      };
      setSystemNotifications(prev => [newLog, ...prev]);

      // 2. Safely figure out the email (protecting against numerical IDs)
      let targetEmail = null;
      if (clientRef.current?.email) {
          targetEmail = clientRef.current.email;
      } else if (typeof baseClient?.id === 'string' && baseClient.id.includes('@')) {
          targetEmail = baseClient.id;
      }

      // 3. Save to Supabase
      if (targetEmail) {
          try {
              const { error } = await supabase.from('crm_system_logs').insert([{
                  client_email: targetEmail,
                  action: action,
                  description: desc, // 🚨 CRITICAL: Maps to the fixed SQL column 'description'
                  actor: userRole || 'Agent',
                  type: type
              }]);

              // Force Supabase to yell at us if it rejects the save
              if (error) {
                  console.error("Supabase rejected the Audit Log:", error.message);
              }
          } catch (e) {
              console.error("Failed to execute audit log save:", e.message);
          }
      } else {
          console.warn("Audit Log skipped: No valid email address found to link to.");
      }
  };

  // --- NEW STATE FOR MANUAL KYC ---
    const [documents, setDocuments] = useState(client.documents || []);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [reviewDoc, setReviewDoc] = useState(null);

  // --- ENHANCED KYC STATE & LOGIC ---
  const [isTradingRestricted, setIsTradingRestricted] = useState(false);

  // --- COMPLIANCE DEPOSIT & EDD VOLUME AGGREGATOR ---
  // 🚨 STRICT RULES PER COMPLIANCE:
  // 1. MUST be a LIVE account (acc.isLive === true, acc.isDemo !== true, no 'demo' in account name/type/group)
  // 2. MUST NOT be a 10X or Prop Firm account (excludes 10x, prop, challenge, funded, evaluation, pioneer accounts)
  // 3. MUST NOT be a child/PAMM account funded by a 10X parent account (e.g. PAMMs with ParentId pointing to 10X)
  // 4. Usernames/emails with '10x' are NOT removed; ONLY the trading accounts themselves are filtered!
  // 5. Legacy P100 tracking is completely deleted (zero double-counting with XValley).
  const calculateXValleyVolume = () => {
      let xValleyTotal = 0;
      const propKeywords = ['10x', 'prop', 'challenge', 'funded', 'evaluation', 'pioneer'];

      if (client.tradingAccounts && Array.isArray(client.tradingAccounts)) {
          // 1. Build lookup map of all client accounts by account ID
          const accountMap = new Map();
          client.tradingAccounts.forEach(acc => {
              const accId = Number(acc.id || acc.accountId);
              if (accId) accountMap.set(accId, acc);
          });

          // 2. Helper to determine if an account is directly 10X / Prop / Challenge / Funded
          const isDirectly10x = (acc) => {
              if (!acc) return false;
              const name = String(acc.name || acc.type || '').toLowerCase();
              const group = String(acc.group || '').toLowerCase();
              return acc.is10x === true || acc.isFunded === true || propKeywords.some(kw => name.includes(kw) || group.includes(kw));
          };

          // 3. Helper to determine if an account is directly Demo
          const isDirectlyDemo = (acc) => {
              if (!acc) return false;
              const name = String(acc.name || acc.type || '').toLowerCase();
              const group = String(acc.group || '').toLowerCase();
              return acc.isDemo === true || !acc.isLive || name.includes('demo') || group.includes('demo');
          };

          // 4. Recursive parent tree checker:
          // If an account was created under / funded by a 10X account (ParentId matches a 10X account),
          // trace up the parent chain so child PAMMs/managed accounts are strictly excluded!
          const isDescendantOfExcluded = (acc, visited = new Set()) => {
              if (!acc || !acc.parentId) return false;
              const parentId = Number(acc.parentId);
              if (!parentId || visited.has(parentId)) return false;
              visited.add(parentId);

              const parent = accountMap.get(parentId);
              if (!parent) return false;
              if (isDirectly10x(parent) || isDirectlyDemo(parent)) return true;
              return isDescendantOfExcluded(parent, visited);
          };

          client.tradingAccounts.forEach(acc => {
              const isDemo = isDirectlyDemo(acc);
              const is10xOrProp = isDirectly10x(acc) || isDescendantOfExcluded(acc);

              if (!isDemo && !is10xOrProp) {
                  xValleyTotal += Number(acc.balance || 0);
              }
          });
      }
      return xValleyTotal;
  };

  // The true live deposit volume from verified real-money trading accounts
  const liveLifetimeVolume = calculateXValleyVolume();

  // Enhanced Due Diligence (EDD) Level Triggers:
  // Level 1 EDD (Proof of Address - POA): Triggers when live real-money volume >= €2,000
  const isLevel1Required = liveLifetimeVolume >= 2000 && client.advancedKycStatus !== 'Completed';
  // Level 2 EDD (Source of Funds - SOF): Triggers when live real-money volume >= €10,000
  const isLevel2Required = liveLifetimeVolume >= 10000 && client.advancedKycStatus !== 'Completed';
  const isEnhancedKycRequired = isLevel1Required;

  // --- DELETE DOCUMENT LOGIC ---
    const handleDeleteDocument = async (docId, e) => {
        e.stopPropagation(); // Prevents the modal from opening when clicking trash
        if(!window.confirm("Are you sure you want to permanently delete this document?")) return;

        try {
            // If it has a '/', it's a real file in our Supabase Vault
            if (docId && typeof docId === 'string' && docId.includes('/')) {
                await supabase.from('crm_documents').delete().eq('file_path', docId);
                await supabase.storage.from('kyc-vault').remove([docId]);
            }
            
            // Remove from UI
            setDocuments(prev => prev.filter(d => d.id !== docId));
            
            // Log it globally so it hits the database
            logSystemActivity('Document Deleted', 'Agent deleted document', 'file');

        } catch (error) {
            console.error("Delete failed:", error);
            alert("Failed to delete document.");
        }
    };

  // --- REAL SUPABASE KYC UPLOAD LOGIC ---
    const handleManualUpload = async (data) => {
        if (!data.file) throw new Error("No file found.");

        // 1. Create a secure, unique file path (e.g. 882991/167890_Passport.pdf)
        const fileExt = data.file.name.split('.').pop();
        const safeFileName = `${Date.now()}_${(data.idType || 'doc').replace(/\s+/g, '')}.${fileExt}`;
        const filePath = `${client.id}/${safeFileName}`;

        // 2. Upload directly to the private Supabase Vault
        const { error: uploadError } = await supabase.storage
            .from('kyc-vault')
            .upload(filePath, data.file);

        if (uploadError) throw new Error("Storage failed: " + uploadError.message);

        // 3. Update the UI instantly with the pending document
        const newDoc = {
            id: filePath,
            type: data.idType || data.category || 'Document',
            status: 'Pending Review', // Real docs start as pending!
            source: 'Admin Upload',
            expiry: data.expiryDate || 'N/A',
            aiScore: null, 
            uploadDate: new Date().toLocaleDateString()
        };
        
        // 🚨 THE FIX 1: Permanently save the document record to the database
        const { error: dbError } = await supabase.from('crm_documents').insert([{
            client_id: String(client.id),
            client_email: client.email,
            file_path: filePath,
            document_type: newDoc.type,
            status: newDoc.status,
            expiry_date: newDoc.expiry,
            source: newDoc.source
        }]);

        if (dbError) {
            console.error("Database save failed:", dbError);
            throw new Error("File uploaded, but database link failed.");
        }

        setDocuments([newDoc, ...documents]);
    };

    // --- APPROVE / REJECT DOCUMENT LOGIC ---
    const handleReviewAction = async (action, reason = '') => {
        let rejectReason = reason;

        try {
            // 1. Update the CRM Document Vault
            if (reviewDoc.id && typeof reviewDoc.id === 'string' && reviewDoc.id.includes('/')) {
                await supabase.from('crm_documents').update({ status: action }).eq('file_path', reviewDoc.id);
            }
            
            // 2. Update Local UI State for the Document
            setDocuments(prev => prev.map(d => d.id === reviewDoc.id ? { ...d, status: action } : d));

            // ==========================================
            // 🚨 AUTOMATION FIRE: Document-Specific Emails
            // ==========================================
            if (action === 'Approved') {
                
                // 1. Check the document name to figure out if it's an Address or ID
                const docName = (reviewDoc.type || '').toLowerCase();
                let exactTriggerId = 'POI_APPROVED'; // Default to ID
                
                if (docName.includes('address') || docName.includes('utility') || docName.includes('bank') || docName.includes('tax')) {
                    exactTriggerId = 'POA_APPROVED';
                }

                // 2. Fire the specific trigger
                await fireAutomationEvent(exactTriggerId, {
                    email: client.email,
                    firstName: client.firstName || client.name?.split(' ')[0] || 'There',
                    documentType: reviewDoc.type 
                });
                
            } else if (action === 'Rejected') {
                let triggerId = 'DOCUMENT_REJECTED';
                if (rejectReason === 'Rejected Details Mismatch') triggerId = 'DOCUMENT_REJECTED_MISMATCH';
                if (rejectReason === 'Rejected Document Quality & Visibility') triggerId = 'DOCUMENT_REJECTED_QUALITY';
                if (rejectReason === 'Rejected Invalid or Expired Document') triggerId = 'DOCUMENT_REJECTED_EXPIRED';

                await fireAutomationEvent(triggerId, {
                    email: client.email,
                    firstName: client.firstName || client.name?.split(' ')[0] || 'There',
                    documentType: reviewDoc.type
                });
                
                // Also visually set the user to rejected so the UI updates
                setClient(prev => ({ ...prev, status: 'kyc_rejected', kycStatus: 'Rejected' }));
                setJourneyStage('kyc_rejected');
            }

            // 3. Log the Action
            const logDescription = action === 'Rejected' 
                ? `Agent manually rejected ${reviewDoc.type}. Reason: ${rejectReason}`
                : `Agent manually approved ${reviewDoc.type}.`;
                
            logSystemActivity(`Document ${action}`, logDescription, 'file');

            setReviewDoc(null);
            alert(`Success! Document marked as ${action} and email dispatched to client.`);

        } catch (err) {
            console.error("CRM Vault Error:", err);
            alert("Network error: Could not reach the CRM backend to update the document status.");
        }
    };

   // --- SECURE DOCUMENT VIEWER ---
    const handleViewDocument = async (doc) => {
        try {
            // Check if this is a real Supabase file (the ID will be the file path containing a '/')
            if (doc.id && typeof doc.id === 'string' && doc.id.includes('/')) {
                // Generate a secure viewing link that expires in 1 hour
                const { data, error } = await supabase.storage
                    .from('kyc-vault')
                    .createSignedUrl(doc.id, 3600);

                if (error) throw error;
                
                // Open the modal and pass the secure temporary link to the viewer
                setReviewDoc({ ...doc, fileUrl: data.signedUrl });
            } 
            // 🚨 THE FIX: Check if it's a live Nommia URL we extracted
            else if (doc.fileUrl) {
                // It's a direct URL from the API, just open it!
                setReviewDoc(doc);
            }
            else {
                // Fallback for old mock data
                setReviewDoc(doc);
            }
        } catch (error) {
            console.error("Error securely loading document:", error);
            alert("Failed to securely load the document.");
        }
    };

// --- 3. DEEP LINK LISTENER (THE MAGIC FIX) ---
  useEffect(() => {
    // A. Switch Tabs automatically
    if (location.state?.activeTab) {
        setActiveTab(location.state.activeTab);
    }
    
    // B. Open Specific Ticket automatically
    // The Fix: We wait until the tickets array actually has data AND is no longer loading
    if (location.state?.ticketId && tickets.length > 0 && !isLiveLoading) {
        
        // In the live database, the ID is stored as 'fullId' to avoid conflicting with the short UI 'id'
        const targetTicket = tickets.find(t => t.fullId === location.state.ticketId || String(t.id) === String(location.state.ticketId));
        
        if (targetTicket) {
            // Give the UI a tiny millisecond to render the tab before opening the workspace
            setTimeout(() => {
                handleOpenTicket(targetTicket);
            }, 50);
        }
    }
  }, [location.state, tickets, isLiveLoading]); // Add isLiveLoading to the dependency array

  // --- HANDLERS ---
  
  const handleUpdateTicketStatus = (ticketId, newStatus) => {
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
  };

  // 🚨 INJECT NEW HANDLER HERE 🚨
  // --- HANDLE END LIVE CHAT ---
  const handleEndChat = async (activeChatId) => {
      if (!activeChatId) return;

      try {
          console.log(`🔒 Closing chat session: ${activeChatId}...`);
          
          const { error } = await supabase
              .from('crm_live_chats')
              .update({ 
                  status: 'Closed', 
                  updated_at: new Date().toISOString() 
              })
              .eq('id', activeChatId);

          if (error) throw error;

          console.log(`✅ Chat ${activeChatId} officially closed. Client UI locked.`);
          
          setCommunications(prev => prev.map(comm => 
              comm.id === activeChatId ? { ...comm, status: 'Closed' } : comm
          ));

          logSystemActivity('Live Chat Ended', `Agent resolved chat session ${activeChatId}`, 'ticket');

      } catch (err) {
          console.error("🔴 Failed to close chat:", err.message);
          alert("Failed to end the chat. Please check your connection.");
      }
  };
  // 🚨 END INJECTION 🚨

  const handleCreateCommunication = async () => {
      if (!newCommData.message && newCommData.template === 'custom') return alert('Message is required');

      try {
          const threadId = `MSG-${Math.floor(Math.random() * 90000) + 10000}`;
          const finalSubject = newCommData.type === 'whatsapp' 
              ? 'WhatsApp Message' 
              : `${newCommData.subject || 'Outbound Message'} [${threadId}]`;

          let finalStatus = 'Sent'; // Default assumption

          // 1. Physically dispatch via Email or WhatsApp
          if (newCommData.type === 'email') {
              const { error: emailError } = await supabase.functions.invoke('send-branded-email', {
                  body: {
                      clientData: client,
                      agentName: userRole || 'Support Team',
                      customMessage: newCommData.useCustomHtml ? newCommData.message : newCommData.message.replace(/\n/g, '<br/>'),
                      emailSubject: finalSubject 
                  }
              });
              
              if (emailError) {
                  console.error("Email Engine Error:", emailError);
                  return alert("Failed to send email: " + emailError.message);
              }
          } 
          else if (newCommData.type === 'whatsapp') {
              const clientPhone = client.phone;
              if (!clientPhone || clientPhone === 'N/A' || clientPhone === 'Unknown') {
                  return alert("Cannot send: Client has no valid phone number on file.");
              }

              try {
                  const waRes = await fetch(`${process.env.REACT_APP_NOMMIA_API_URL}/api/wa/send-message`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ phone: clientPhone, message: newCommData.message })
                  });
                  
                  const waData = await waRes.json();
                  
                  // 🚨 THE FIX: Catch the Bizbot error without crashing the CRM!
                  if (waData.status !== true) {
                      console.warn("Nommia Bizbot Rejection:", waData.message);
                      finalStatus = 'Failed'; // Mark the UI badge as failed
                      alert(`Warning: Message saved to CRM, but Nommia failed to deliver to WhatsApp.\n\nBackend Error: ${waData.message}`);
                  }
              } catch (waErr) {
                  console.error("WhatsApp Network Error:", waErr);
                  finalStatus = 'Failed';
              }
          }

          // 2. Permanently save the log to the dedicated Communications table (Even if delivery failed!)
          const { data: dbData, error: dbError } = await supabase
              .from('crm_communications')
              .insert([{
                  id: threadId, 
                  client_id: String(client.id),
                  client_email: client.email,
                  client_name: client.name,
                  type: newCommData.type,
                  direction: 'outbound',
                  subject: finalSubject,
                  body: newCommData.message,
                  agent_name: userRole || 'Support Agent',
                  status: finalStatus // Uses 'Sent' or 'Failed'
              }])
              .select()
              .single();

          if (dbError) throw dbError;

          // 3. Instantly update the UI without needing to refresh
          const newComm = {
              id: dbData?.id || `COM-${Date.now()}`,
              type: newCommData.type,
              subject: dbData?.subject || newCommData.subject,
              date: 'Just Now',
              status: finalStatus,
              messages: [{ sender: userRole || 'Staff', text: newCommData.message, time: 'Just Now' }]
          };

          setCommunications([newComm, ...communications]);
          logSystemActivity(`Logged ${newCommData.type}`, `Subject: ${newCommData.subject || 'N/A'}`, 'edit');

          setShowNewCommModal(false);
          setNewCommData({ type: 'email', template: 'custom', subject: '', message: '', useCustomHtml: false });
          
          if (finalStatus === 'Sent') {
              alert(`Message successfully sent and saved to the database!`);
          }

      } catch (error) {
          console.error("Database Save Error:", error);
          alert("Error: The process crashed while attempting to save to the database.");
      }
  };

  useEffect(() => {
      if (newCommData.template !== 'custom' && liveTemplates[newCommData.type]) {
          const tmpl = liveTemplates[newCommData.type].find(t => t.id === newCommData.template);
          if (tmpl) {
              setNewCommData(prev => ({ 
                  ...prev, 
                  subject: tmpl.subject || prev.subject, 
                  message: tmpl.body || '',
                  useCustomHtml: tmpl.use_custom_html || false // Catch the custom flag!
              }));
          }
      }
  }, [newCommData.template, newCommData.type, liveTemplates]);

  const selectedComm = communications?.find(c => c.id === selectedCommId) || null;

  const handleOpenTicket = (ticket) => {
      if (ticket.status === 'New') {
          const updatedTickets = tickets.map(t => 
              t.id === ticket.id ? { ...t, status: 'Open' } : t
          );
          setTickets(updatedTickets);
      }
      setSelectedTicket(ticket);
  };

// --- HANDLER: TICKET ACTIVITY (Full Version with Master Status Sync) ---
  const handleTicketActivity = (type, data) => {
    // 1. Safety Guard
    if (!data || (!data.action && !data.text && !data.transcript)) return;

    // 2. Prepare Message History
    let conversationHistory = [];
    if (data.transcript && data.transcript.length > 0) {
      conversationHistory = data.transcript.map(msg => ({
        sender: ['me', 'staff', 'agent'].includes(msg.sender?.toLowerCase()) ? 'Staff' : 'Client',
        text: msg.text,
        time: msg.timestamp || 'Just Now'
      }));
    } else {
      const actionText = data.action 
        ? `Action: ${data.action} ${data.reason ? `(${data.reason})` : ''}` 
        : (data.text || "Ticket Updated");
      conversationHistory = [{ sender: 'System', text: actionText, time: 'Just Now' }];
    }

    // 3. 🚨 THE FIX: Master Status Resolver
    // We strictly trust the exact status passed by the workspace (Open, Closed, Hold, etc.)
    let resolvedStatus = data.status;
    if (!resolvedStatus) {
        // Fallback just in case
        const isClosing = data.action === 'close' || data.action === 'Closed' || data.newStatus === 'Closed' || (data.text && data.text.includes('Closed'));
        resolvedStatus = isClosing ? 'Closed' : undefined;
    }

    // 4. Update the Communications List (Inbox)
    setCommunications(prev => {
      const targetId = data.ticketId || selectedTicket?.id;
      const existingIndex = prev.findIndex(c => String(c.id) === String(targetId));

      if (existingIndex >= 0) {
        const updatedComms = [...prev];
        updatedComms[existingIndex] = {
          ...updatedComms[existingIndex],
          // Apply the resolved status so the inbox badge updates instantly
          status: resolvedStatus || updatedComms[existingIndex].status,
          date: 'Just Now',
          subject: data.ticketSubject ? `[Ticket] ${data.ticketSubject}` : updatedComms[existingIndex].subject,
          messages: conversationHistory
        };
        return updatedComms;
      } else {
        return [{
          id: targetId,
          type: 'ticket',
          subject: data.ticketSubject || 'Support Ticket',
          status: resolvedStatus || 'Open',
          date: 'Just Now',
          messages: conversationHistory
        }, ...prev];
      }
    });

    // 5. Update the Tickets List
    setTickets(prev => prev.map(t => {
        const targetId = data.ticketId || selectedTicket?.fullId || selectedTicket?.id;
        
        if (String(t.id) === String(targetId) || String(t.fullId) === String(targetId)) {
            return {
                ...t,
                // 🚨 Apply the resolved status so the master list memory updates instantly!
                status: resolvedStatus || t.status,
                history: data.transcript ? data.transcript : t.history
            };
        }
        return t;
    }));

    // 6. Update Global Audit Log
    if (!data.transcript) { 
        const logText = data.action ? `Action: ${data.action} ${data.reason ? `(${data.reason})` : ''}` : (data.text || "Ticket Updated");
        logSystemActivity(resolvedStatus === 'Closed' ? 'Ticket Closed' : 'Ticket Updated', logText, 'ticket');
    }
  };

  // --- HANDLE SECURITY ACTIONS ---
  const handleSecurityAction = (action, impact) => {
      // Add this line to mirror the action to the global audit log:
      logSystemActivity(action, 'Agent triggered security protocol', 'security'); 
      const newEvent = {
          id: Date.now(),
          type: action,
          details: 'Action triggered by Agent',
          location: 'Internal HQ',
          ip: '192.168.1.55', // Mock Agent IP
          time: 'Just Now',
          risk: impact > 0 ? 'High' : 'Low' 
      };

      setSecurityEvents([newEvent, ...securityEvents]);
      
      // Update Risk Score (Clamp between 0 and 100)
      setRiskScore(prev => {
          const newScore = prev + impact;
          return Math.max(0, Math.min(100, newScore));
      });
  };

  const handleCreateTicket = async () => {
      if (!newTicketData.subject.trim()) return alert("Subject is required");

      const timestamp = new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      
      const initialHistory = [{ 
          id: 1, 
          type: 'system', 
          sender: 'auto', 
          text: `**Ticket Created**\nSubject: ${newTicketData.subject}\nCategory: ${newTicketData.category}\n\n**Internal Notes:**\n${newTicketData.notes || 'No initial notes provided.'}`, 
          timestamp: timestamp 
      }];

      try {
          const { data: { user } } = await supabase.auth.getUser();

          // 1. Insert into Supabase
          const { data: dbTicket, error } = await supabase
              .from('crm_tickets')
              .insert([{
                  client_id: String(client.id),
                  client_email: client.email,
                  client_name: client.name,
                  subject: newTicketData.subject,
                  category: newTicketData.category,
                  description: newTicketData.notes,
                  status: 'New',
                  priority: newTicketData.priority,
                  history: initialHistory,
                  assigned_to: user ? user.id : 'unassigned'
              }])
              .select()
              .single();

          if (error) throw error;

          const newTicketId = dbTicket.id;
          const shortId = newTicketId.length > 8 ? newTicketId.substring(0, 8) : newTicketId;

          // 2. Update Local UI State
          const newTicket = {
              id: shortId,
              fullId: newTicketId,
              subject: newTicketData.subject,
              category: newTicketData.category,
              description: newTicketData.notes,
              status: 'New',
              priority: newTicketData.priority,
              created: 'Just Now',
              assignedTo: user ? user.id : 'unassigned',
              client: { name: client.name },
              history: initialHistory
          };

          const newCommEntry = {
              id: shortId,
              type: 'ticket', 
              subject: `[Ticket] ${newTicketData.subject}`, 
              date: 'Just Now',
              status: 'Open',
              messages: [{ sender: 'System', text: 'Ticket Created', time: timestamp }]
          };

          setTickets(prev => [newTicket, ...prev]);
          setCommunications(prev => [newCommEntry, ...prev]);
          
          logSystemActivity('Ticket Created', `Subject: ${newTicketData.subject}`, 'ticket');
          
          setNewTicketData({ subject: '', category: 'General', priority: 'Medium', notes: '' });
          setShowNewTicketModal(false);
          
      } catch (err) {
          console.error("Failed to create ticket in database:", err);
          alert("Error: Could not save the ticket to the database.");
      }
  };

  // --- HANDLE CLOSE (Check if we should navigate back or close modal) ---
  const handleClose = () => {
      if (onClose) {
          onClose(); // Used when opened as Modal
      } else {
          navigate('/'); // Used when opened as Page
      }
  };

  // --- RBAC & TIER SYNC ---
  const handleTierUpdate = async (newTier) => {
      try {
          // 1. Push the update instantly to the Supabase profiles table
          const { error } = await supabase
              .from('profiles')
              .update({ membership_tier: newTier })
              .eq('id', client.id); // Ensures we only update this specific client

          if (error) throw error;

          // 2. Update the local UI state so the agent sees it change immediately
          setClient({ ...client, membership_tier: newTier });
          
          // 3. Log it in the CRM Audit Trail
          logSystemActivity('Tier Upgrade', `Account upgraded to ${newTier}`, 'edit');
          
          alert(`Success! Client is now on the ${newTier} tier. Features are unlocked.`);
      } catch (err) {
          console.error("Error updating membership tier:", err);
          alert("Failed to update membership tier. Check console.");
      }
  };

// --- UPDATE CLIENT DETAILS ---
  const handleUpdateClientDetails = async (updatedFields) => {
      try {
          // 🚨 THE FIX: Sanitize the DOB so "N/A" or empty strings are sent as `null`
          const safeDob = (!updatedFields.dob || updatedFields.dob === 'N/A') ? null : updatedFields.dob;

          const res = await authFetch(`https://nommia-crm-backend-production.up.railway.app/api/crm/clients/update-profile`, {
              method: 'POST',
              body: JSON.stringify({ 
                  email: client.email, 
                  profileData: { ...updatedFields, dob: safeDob } // Pass the sanitized date
              })
          });
          
          const data = await res.json();
          if (!data.success) throw new Error(data.message);

          // Update local UI state
          setClient(prev => ({
              ...prev,
              ...updatedFields,
              dob: safeDob || 'N/A', 
              name: `${updatedFields.firstName} ${updatedFields.lastName}`.trim(),
              // 👇 ADDED THIS BLOCK TO SYNC ADDRESS 👇
              address: {
                  ...prev.address,
                  street: updatedFields.street,
                  city: updatedFields.city,
                  postCode: updatedFields.postCode,
                  country: updatedFields.country 
              }
          }));

          // Fire Audit Log
          logSystemActivity('Profile Details Updated', `Agent manually updated PII/Summary details.`, 'edit');

          return { success: true };
      } catch (error) {
          console.error("Failed to update client details:", error);
          return { success: false, message: error.message };
      }
  };

  const handleForceProvision = async () => {
      const tempPassword = window.prompt("Enter a temporary password for this user (Must have 1 uppercase, 1 number, 1 special character):", "NommiaSecure123!");
      if (!tempPassword) return;

      setIsProvisioning(true); // 👈 START FULLSCREEN LOADER

      try {
          const response = await authFetch(`https://nommia-crm-backend-production.up.railway.app/api/crm/clients/force-provision`, {
              method: 'POST',
              body: JSON.stringify({ email: client.email, tempPassword: tempPassword })
          });

          const data = await response.json();
          if (data.success) {
              logSystemActivity('Account Provisioned', 'Agent manually force-provisioned XValley MT5 account.', 'system');
              alert("Success! XValley Account Created and Supabase Password synced. Please refresh the page.");
          } else {
              alert(`Failed: ${data.message}`);
          }
      } catch (error) {
          console.error("Provisioning error:", error);
          alert("Network error occurred while trying to provision the account.");
      } finally {
          setIsProvisioning(false); // 👈 STOP FULLSCREEN LOADER
      }
  };

  return (
    // Updated CSS to handle both Page Mode (z-0 relative) and Modal Mode (fixed z-60)
    <div className={onClose ? "fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex justify-end z-[60] transition-opacity" : "min-h-screen bg-slate-950 flex flex-col"}>
      <div className={onClose ? "w-full md:w-[85vw] bg-slate-900/95 h-full shadow-2xl overflow-y-auto animate-slide-in-right border-l border-slate-700 backdrop-blur-xl flex flex-col relative" : "w-full h-full flex flex-col relative"}>
        
        {isProvisioning && (
            <div className="fixed inset-0 z-[100] bg-slate-950/90 flex flex-col items-center justify-center p-4 backdrop-blur-md animate-fade-in">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6 shadow-[0_0_15px_rgba(37,99,235,0.5)]"></div>
                <h3 className="text-2xl font-bold text-white mb-2 tracking-wide flex items-center gap-3">
                    <Server className="text-blue-500" size={24} /> 
                    Provisioning Trading Environment
                </h3>
                <p className="text-blue-400 font-mono text-sm animate-pulse">Establishing secure bridge to XValley...</p>
                <p className="text-slate-500 text-xs mt-6 px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg">
                    This securely syncs databases and usually takes ~30 seconds. Please do not close this window.
                </p>
            </div>
        )}

        {/* --- MANUAL KYC MODALS --- */}
        <ManualUploadModal
            isOpen={showUploadModal} 
            onClose={() => setShowUploadModal(false)} 
            onUpload={handleManualUpload} 
        />
        <DocumentReviewModal 
            isOpen={!!reviewDoc} 
            onClose={() => setReviewDoc(null)} 
            doc={reviewDoc} 
            onAction={handleReviewAction} 
        />
        <DiditMediaModal
            isOpen={showDiditMediaModal}
            onClose={() => setShowDiditMediaModal(false)}
            media={diditMedia}
            isLoading={isLoadingDiditMedia}
        />

        {/* --- MODALS SECTION --- */}
        {showNewTicketForm && (
            <div className="absolute inset-0 bg-slate-950/90 z-50 flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in">
                    <h3 className="text-xl font-bold text-white mb-6">Open New Support Ticket</h3>
                    <div className="space-y-4">
                        <div><label className="text-sm font-bold text-slate-400 block mb-2">Subject</label><input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white" value={newTicketData.subject} onChange={(e) => setNewTicketData({...newTicketData, subject: e.target.value})} /></div>
                        <div><label className="text-sm font-bold text-slate-400 block mb-2">Priority</label><select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white" value={newTicketData.priority} onChange={(e) => setNewTicketData({...newTicketData, priority: e.target.value})}><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select></div>
                        <div className="pt-4 flex gap-3"><button onClick={() => setShowNewTicketForm(false)} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg">Cancel</button><button onClick={handleCreateTicket} className="flex-1 py-3 bg-amber-500 text-slate-900 font-bold rounded-lg">Create</button></div>
                    </div>
                </div>
            </div>
        )}

        {showNewCommModal && (
            <div className="absolute inset-0 bg-slate-950/90 z-50 flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl p-6 animate-fade-in flex flex-col max-h-[90vh]">
                    
                    {/* Header & Tabs */}
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold text-white">New Communication</h3>
                        {newCommData.type === 'email' && (
                            <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
                                <button onClick={() => setShowCommPreview(false)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${!showCommPreview ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}>Write</button>
                                <button onClick={() => setShowCommPreview(true)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${showCommPreview ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}>Live Preview</button>
                            </div>
                        )}
                        <button onClick={() => { setShowNewCommModal(false); setShowCommPreview(false); }} className="text-slate-400 hover:text-white"><X size={20}/></button>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                        {!showCommPreview ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                     <button onClick={() => setNewCommData({...newCommData, type: 'email', template: 'custom', message: ''})} className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${newCommData.type === 'email' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}><Mail size={24} /><span className="text-sm font-bold">Email</span></button>
                                     <button onClick={() => { setNewCommData({...newCommData, type: 'whatsapp', template: 'custom', message: ''}); setShowCommPreview(false); }} className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${newCommData.type === 'whatsapp' ? 'bg-green-600 border-green-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}><MessageCircle size={24} /><span className="text-sm font-bold">WhatsApp</span></button>
                                </div>
                                
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Saved Templates</label>
                                    <select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={newCommData.template} onChange={(e) => setNewCommData({...newCommData, template: e.target.value})}>
                                        <option value="custom">-- Write Custom Message --</option>
                                        {liveTemplates[newCommData.type]?.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                    </select>
                                </div>
                                
                                {newCommData.type === 'email' && (
                                    <div>
                                        <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Subject</label>
                                        <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={newCommData.subject} onChange={(e) => setNewCommData({...newCommData, subject: e.target.value})} />
                                    </div>
                                )}
                                
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Message Content</label>
                                    <textarea className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm min-h-[150px] focus:ring-2 focus:ring-blue-500 outline-none" value={newCommData.message} onChange={(e) => setNewCommData({...newCommData, message: e.target.value})} />
                                </div>
                            </div>
                        ) : (
                            /* Live Preview Engine */
                            <div className="h-[500px] flex flex-col border border-slate-700 rounded-lg overflow-hidden mt-2 shadow-2xl">
                                <div className="bg-slate-950 p-3 flex items-center gap-2 text-xs text-slate-500 border-b border-slate-800">
                                    <div className="w-3 h-3 rounded-full bg-red-500/20"></div>
                                    <div className="w-3 h-3 rounded-full bg-amber-500/20"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/20"></div>
                                    <span className="ml-2 font-mono text-[10px]">Client Inbox Preview - Variables Auto-Filled</span>
                                </div>
                                <div className="flex-1 bg-white overflow-hidden">
                                    <iframe 
                                        title="email-preview"
                                        className="w-full h-full border-0"
                                        srcDoc={(newCommData.useCustomHtml ? newCommData.message : masterHtml.replace(/{{CONTENT_BODY}}/g, newCommData.message.replace(/\n/g, '<br/>')))
                                            .replace(/{{client_first_name}}/g, client.firstName || 'Client')
                                            .replace(/{{client_last_name}}/g, client.lastName || '')
                                            .replace(/{{client_email}}/g, client.email || '')
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pt-4 mt-4 flex gap-3 border-t border-slate-800">
                        <button onClick={() => { setShowNewCommModal(false); setShowCommPreview(false); }} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors">Cancel</button>
                        <button onClick={handleCreateCommunication} className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
                            <Send size={16} /> Send to Client
                        </button>
                    </div>
                </div>
            </div>
        )}

      {showNewTicketModal && (
          <div className="absolute inset-0 z-[70] bg-slate-950/90 flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">Create New Ticket</h3>
                      <button onClick={() => setShowNewTicketModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="space-y-4">
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Subject / Title</label>
                          <input 
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="e.g. Wire Transfer Delay..."
                              value={newTicketData.subject}
                              onChange={(e) => setNewTicketData({...newTicketData, subject: e.target.value})}
                              autoFocus
                          />
                      </div>
                      
                      <div className="flex gap-4">
                          <div className="flex-1">
                              <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Category</label>
                              <select 
                                  className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                  value={newTicketData.category}
                                  onChange={(e) => setNewTicketData({...newTicketData, category: e.target.value})}
                              >
                                  <option>General</option>
                                  <option>Compliance</option>
                                  <option>Payments</option>
                                  <option>Trading</option>
                                  <option>Technical</option>
                              </select>
                          </div>
                          <div className="flex-1">
                              <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Priority</label>
                              <select 
                                  className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                  value={newTicketData.priority}
                                  onChange={(e) => setNewTicketData({...newTicketData, priority: e.target.value})}
                              >
                                  <option>Low</option>
                                  <option>Medium</option>
                                  <option>High</option>
                                  <option>Critical</option>
                              </select>
                          </div>
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Internal Context / Agent Notes</label>
                          <textarea 
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]"
                              placeholder="Why are you creating this ticket? These notes will be visible to other agents in the ticket chat."
                              value={newTicketData.notes}
                              onChange={(e) => setNewTicketData({...newTicketData, notes: e.target.value})}
                          />
                      </div>

                      <div className="pt-4 flex gap-3">
                          <button onClick={() => setShowNewTicketModal(false)} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors">Cancel</button>
                          <button onClick={handleCreateTicket} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 transition-colors">Create Ticket</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

        {/* --- MAIN PROFILE HEADER --- */}
        <div className="bg-slate-900/95 border-b border-slate-800 px-4 md:px-8 py-4 md:py-6 sticky top-0 z-20 flex justify-between items-center backdrop-blur-md">
          <div className="flex items-center space-x-3 md:space-x-5">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 flex items-center justify-center text-white text-lg md:text-xl font-bold shadow-inner flex-shrink-0">{client.avatar}</div>
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-white tracking-tight leading-tight flex items-center gap-3">
    {client.name}
    {isLiveLoading && <RefreshCw size={18} className="text-slate-500 animate-spin" title="Fetching live data from Nommia..." />}
</h2>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1 md:mt-2">
                {/* --- MEMBERSHIP TIER RBAC DROPDOWN --- */}
                <div className="flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-1 transition-colors hover:border-amber-500/50">
                    <Activity size={12} className="text-amber-500 md:w-[14px] md:h-[14px]" />
                    <select
                        value={client.membership_tier || client.tier || 'Tier 1'}
                        onChange={(e) => handleTierUpdate(e.target.value)}
                        className="bg-transparent text-amber-500 text-xs md:text-sm font-bold outline-none cursor-pointer appearance-none pl-1"
                        title="Change Client Tier"
                    >
                        <option value="Tier 1" className="bg-slate-900 text-white">Tier 1 (Standard)</option>
                        <option value="Pro" className="bg-slate-900 text-white">Pro (Advanced)</option>
                        <option value="Elite" className="bg-slate-900 text-white">Elite (Full Access)</option>
                    </select>
                </div>

                {client.role === 'IB' && <span className="text-xs md:text-sm bg-blue-500/20 text-blue-400 px-2 rounded border border-blue-500/30">Partner</span>}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-800 rounded-full transition-colors group"><XCircle className="text-slate-500 group-hover:text-white" size={28} /></button>
        </div>

        {/* --- AD-HOC BROKER PROFILE SYNC HEALER --- */}
        {client.is_missing_broker_row && (
            <div className="mx-4 md:mx-8 mt-6 w-auto bg-red-500/10 border border-red-500/30 p-4 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-pulse">
                <div>
                    <p className="text-xs font-bold text-red-400 uppercase tracking-widest flex items-center gap-1.5">
                        <ShieldX size={14} /> Critical Database Out-of-Sync Anomaly
                    </p>
                    <p className="text-slate-400 text-xs mt-1">This user is completely missing from user_broker_accounts. Standard trade execution and CRM pipelines will fail.</p>
                </div>
                <button 
                    onClick={async () => {
                        if (!window.confirm(`Force back-provision broker pipeline and initialize CRM data rows for ${client.name}?`)) return;
                        try {
                            const isNumericRef = client.referred_by && /^\d+$/.test(String(client.referred_by).trim());
                            let generatedOwnCode;
                            let assignedCommunityId = client.community_id || null;

                            if (isNumericRef || assignedCommunityId === '795f07ab-9e7b-4586-ae30-9267fd8526b2') {
                                assignedCommunityId = '795f07ab-9e7b-4586-ae30-9267fd8526b2';
                                const { data: maxRows } = await supabase
                                    .from('user_broker_accounts')
                                    .select('referral_code')
                                    .gte('referral_code', '1000000')
                                    .order('referral_code', { ascending: false })
                                    .limit(20);
                                let maxNum = 1000000;
                                if (maxRows && maxRows.length > 0) {
                                    for (const r of maxRows) {
                                        const v = parseInt(r.referral_code, 10);
                                        if (!isNaN(v) && v >= 1000000 && v > maxNum) maxNum = v;
                                    }
                                }
                                generatedOwnCode = String(maxNum + 1);
                            } else {
                                generatedOwnCode = Math.random().toString(36).substring(2, 8).toUpperCase();
                            }

                            const { error: healError } = await supabase.from('user_broker_accounts').insert([{
                                user_id: client.id, 
                                email: client.email.toLowerCase(),
                                broker_name: 'xvalley',
                                name: client.name,
                                first_name: client.firstName || client.name?.split(' ')[0] || '',
                                last_name: client.lastName || client.name?.split(' ')[1] || '',
                                phone: client.phone || '+440000000000',
                                referral_code: generatedOwnCode,
                                referred_by: client.referred_by || null,
                                community_id: assignedCommunityId,
                                kyc_approved: client.kycStatus === 'Approved' || client.kycStatus === 'VERIFIED',
                                kyc_status: (client.kycStatus === 'Approved' || client.kycStatus === 'VERIFIED') ? 'approved' : 'unverified',
                                is_active: true
                            }]);

                            if (healError) throw healError;
                            
                            if (assignedCommunityId) {
                                await supabase.from('profiles').update({ community_id: assignedCommunityId }).eq('email', client.email.toLowerCase());
                            }

                            if (generatedOwnCode && /^\d+$/.test(generatedOwnCode)) {
                                try {
                                    const sId = parseInt(client.referred_by, 10);
                                    let s1 = 0, s2 = 0, s3 = 0, s4 = 0, s5 = 0, s6 = 0, s7 = 0, s8 = 0, master_ib = 0;
                                    if (!isNaN(sId) && sId > 0) {
                                        const { data: sNode } = await supabase.from('defily_tree_nodes').select('*').eq('nft_id', sId).maybeSingle();
                                        if (sNode) {
                                            s1 = sNode.nft_id;
                                            s2 = sNode.sponsor1 || 0;
                                            s3 = sNode.sponsor2 || 0;
                                            s4 = sNode.sponsor3 || 0;
                                            s5 = sNode.sponsor4 || 0;
                                            s6 = sNode.sponsor5 || 0;
                                            s7 = sNode.sponsor6 || 0;
                                            s8 = sNode.sponsor7 || 0;
                                            master_ib = sNode.master_ib || 0;
                                        } else {
                                            s1 = sId;
                                        }
                                    }
                                    await supabase.from('defily_tree_nodes').upsert({
                                        nft_id: parseInt(generatedOwnCode, 10),
                                        sponsor1: s1,
                                        sponsor2: s2,
                                        sponsor3: s3,
                                        sponsor4: s4,
                                        sponsor5: s5,
                                        sponsor6: s6,
                                        sponsor7: s7,
                                        sponsor8: s8,
                                        master_ib: master_ib,
                                        email: client.email.toLowerCase(),
                                        user_id: client.id
                                    }, { onConflict: 'nft_id' });
                                } catch (treeErr) {
                                    console.warn('DeFily tree node registration warning:', treeErr);
                                }
                            }
                            
                            // Background fetch to establish account link inside XValley API
                            fetch(`https://innovative-rebirth-production.up.railway.app/api/xvalley/register`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    firstName: client.firstName || 'Nommia',
                                    lastName: client.lastName || 'Trader',
                                    email: client.email,
                                    phone: client.phone || '+440000000000',
                                    password: 'NommiaSecureMigration2026!', 
                                    countryIso: 'GB', 
                                    companyId: 5,
                                    accountTypeId: 14,
                                    ownReferralCode: generatedOwnCode
                                })
                            }).catch(e => console.warn("Background XValley Sync warning:", e));

                            // Assuming logSystemActivity exists in this scope
                            if (typeof logSystemActivity === 'function') {
                                logSystemActivity('Profile Row Repaired', 'Agent manually executed database healing protocol.', 'system');
                            }
                            
                            alert("Success! User successfully mapped into CRM warehouse. Please refresh the page to load updated execution channels.");
                        } catch (err) {
                            console.error("Manual healing failed:", err);
                            alert("Error: Core schema rejection. " + err.message);
                        }
                    }}
                    className="bg-red-600 hover:bg-red-500 text-white font-bold text-xs px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 shrink-0 shadow-lg"
                >
                    <RefreshCw size={12} /> Force CRM Repair
                </button>
            </div>
        )}

        {/* --- TABS NAVIGATION --- */}
        <div className="sticky top-[80px] md:top-[112px] z-10 bg-slate-900/95 border-b border-slate-800">
            <div className="px-4 md:px-8 flex space-x-6 md:space-x-8 overflow-x-auto no-scrollbar">
                {['overview', 'documents', 'tickets', 'disputes', 'security', 'comms', ...(client.role === 'IB' ? ['network'] : [])].map(tab => (
                    <button key={tab} onClick={() => { setActiveTab(tab); setSelectedTicket(null); }} className={`py-4 md:py-5 text-sm font-bold border-b-2 transition-all capitalize tracking-wide whitespace-nowrap flex-shrink-0 ${activeTab === tab ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>
                        {tab}
                    </button>
                ))}
            </div>
        </div>

        {/* --- TAB CONTENT AREA --- */}
        <div className="p-4 md:p-8 pb-20 flex-1 flex flex-col">
          
        {activeTab === 'overview' && (
             <OverviewTab 
                 key={`overview-${client.status}-${journeyStage}`}
                 client={{
                     ...client, 
                     journeyStage: journeyStage,
                     currentStageIndex: currentStageIndex,
                     systemNotifications: systemNotifications 
                 }} 
                 journeyStage={journeyStage}
                 currentStageIndex={currentStageIndex}
                 liveStages={LIVE_JOURNEY_STAGES}
                 userRole={userRole || 'Sales_L1'} 
                 loggedInAgent={currentUser?.name || 'System Agent'} 
                 onForceProvision={handleForceProvision}
                 onUpdateClient={handleUpdateClientDetails}
             />
          )}

{/* === DOCUMENTS TAB === */}
        {activeTab === 'documents' && (
            <div className="space-y-8 animate-fade-in"> 
                
                {/* 0. Automated Didit Verification & AML Compliance Vault */}
                <div className="bg-slate-800/60 p-6 rounded-2xl border border-blue-500/30 shadow-xl backdrop-blur-sm space-y-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-slate-700/60 pb-5">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400">
                                <ShieldCheck size={26} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    Automated Compliance & Verification (Didit)
                                </h3>
                                <p className="text-xs text-blue-300/80 mt-0.5">
                                    Real-time biometric identity verification, AML/PEP sanctions screening, and continuous 24/7 monitoring
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                                (client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                                ['declined', 'rejected'].includes(String(client.kyc_status).toLowerCase()) ? 'bg-red-500/20 text-red-400 border border-red-500/40' :
                                ['pending', 'in_review', 'in_progress'].includes(String(client.kyc_status).toLowerCase()) ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' :
                                ['expired', 'abandoned'].includes(String(client.kyc_status).toLowerCase()) ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' :
                                'bg-slate-700/60 text-slate-400 border border-slate-600'
                            }`}>
                                {(client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') && <CheckCircle size={14} />}
                                {['declined', 'rejected'].includes(String(client.kyc_status).toLowerCase()) && <XCircle size={14} />}
                                {['pending', 'in_review', 'in_progress'].includes(String(client.kyc_status).toLowerCase()) && <Clock size={14} />}
                                {client.kyc_status || 'Unverified'}
                            </span>
                            <button
                                onClick={handleOpenDiditMedia}
                                disabled={isLoadingDiditMedia}
                                title="View verified client ID document photos and 3D biometric selfie"
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50"
                            >
                                <Eye size={13} />
                                <span>{isLoadingDiditMedia ? "Loading..." : "View ID & Biometrics"}</span>
                            </button>
                            <button
                                onClick={handleSyncDidit}
                                disabled={isSyncingDidit}
                                title="Sync latest KYC and AML screening decision from Didit"
                                className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 border border-blue-500/40 rounded-xl text-xs font-bold flex items-center gap-2 transition-all disabled:opacity-50 shadow-sm"
                            >
                                <RefreshCw size={13} className={isSyncingDidit ? "animate-spin" : ""} />
                                <span>{isSyncingDidit ? "Syncing..." : "Sync Live Decision"}</span>
                            </button>
                        </div>
                    </div>

                    {/* Rejection / Decline Warning Banner */}
                    {(['declined', 'rejected'].includes(String(client.kyc_status).toLowerCase()) || client.kyc_rejection_reason) && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
                            <AlertOctagon size={20} className="text-red-400 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm font-bold text-red-400">Verification Declined by Compliance Engine</div>
                                <div className="text-xs text-red-200/90 mt-1 font-medium">
                                    Reason: {client.kyc_rejection_reason || 'Document validation check failed or biometric liveness score insufficient.'}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1">
                                    The client can restart automated verification on their dashboard or upload high-resolution manual identity documents below.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 4 Core Results KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* 1. KYC Identity Verification Results */}
                        <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-700/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                        <ScanFace size={14} className="text-blue-400" /> Identity Document
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                        (client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') ? 'bg-emerald-500/20 text-emerald-400' :
                                        ['declined', 'rejected'].includes(String(client.kyc_status).toLowerCase()) ? 'bg-red-500/20 text-red-400' :
                                        'bg-slate-800 text-slate-400'
                                    }`}>
                                        {(client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') ? 'Verified' : (client.kyc_status || 'Pending')}
                                    </span>
                                </div>
                                <div className="text-base font-bold text-white mt-1">
                                    {client.kyc_document_type || (String(client.kyc_status).toLowerCase() === 'approved' ? 'Verified ID' : 'Document Not Provided')}
                                </div>
                                <div className="text-xs text-slate-400 mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Issuing Country:</span>
                                        <span className="font-semibold text-slate-200">{client.kyc_country || client.country || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Verification:</span>
                                        <span className="font-semibold text-slate-200">{client.kyc_human_reviewed ? 'Human Officer' : 'AI Biometric & Liveness'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500">
                                <span>Completed: {client.kyc_completed_at ? new Date(client.kyc_completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Awaiting completion'}</span>
                                <button
                                    onClick={handleOpenDiditMedia}
                                    className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1 transition-colors"
                                >
                                    <Eye size={12} /> View Photos
                                </button>
                            </div>
                        </div>

                        {/* 2. AML / PEP Screening Results */}
                        <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-700/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                        <ShieldAlert size={14} className={client.aml_hits > 0 ? "text-red-400" : "text-emerald-400"} /> AML & Sanctions
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                        client.aml_hits === 0 ? 'bg-emerald-500/20 text-emerald-400' :
                                        client.aml_hits > 0 ? 'bg-red-500/20 text-red-400' :
                                        'bg-slate-800 text-slate-400'
                                    }`}>
                                        {client.aml_hits === 0 ? 'Passed' : client.aml_hits > 0 ? 'Flagged' : 'Pending'}
                                    </span>
                                </div>
                                <div className="text-base font-bold mt-1">
                                    {client.aml_hits === 0 ? (
                                        <span className="text-emerald-400 flex items-center gap-1.5">
                                            <CheckCircle size={16} /> Clean (0 Hits)
                                        </span>
                                    ) : client.aml_hits > 0 ? (
                                        <span className="text-red-400 flex items-center gap-1.5">
                                            <AlertOctagon size={16} /> {client.aml_hits} Flagged Hits
                                        </span>
                                    ) : (
                                        <span className="text-slate-300">
                                            {client.kyc_status ? 'Screened Clean' : 'Pending Check'}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-400 mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span>PEP Status:</span>
                                        <span className="font-semibold text-emerald-400">Not Listed</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Sanctions List:</span>
                                        <span className="font-semibold text-emerald-400">Clear (UN/EU/OFAC)</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 mt-3 border-t border-slate-800/80 text-[11px] text-slate-500">
                                Screened: {client.aml_screened_at ? new Date(client.aml_screened_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : (client.kyc_completed_at ? new Date(client.kyc_completed_at).toLocaleDateString('en-GB') : 'Pending')}
                            </div>
                        </div>

                        {/* 3. Ongoing Screening Results */}
                        <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-700/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                        <Activity size={14} className="text-indigo-400" /> Ongoing Screening
                                    </span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Active
                                    </span>
                                </div>
                                <div className="text-base font-bold text-indigo-300 mt-1 flex items-center gap-1.5">
                                    24/7 Automated Monitoring
                                </div>
                                <div className="text-xs text-slate-400 mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Frequency:</span>
                                        <span className="font-semibold text-slate-200">Daily Delta Scan</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Watchlists:</span>
                                        <span className="font-semibold text-slate-200">Global Sanctions & PEP</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 mt-3 border-t border-slate-800/80 text-[11px] text-slate-500">
                                Status: {client.aml_hits > 0 ? 'Action Required' : 'Continuous Compliance'}
                            </div>
                        </div>

                        {/* 4. Session & Audit Trail */}
                        <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-700/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                        <Key size={14} className="text-purple-400" /> Verification Session
                                    </span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">
                                        Didit Engine
                                    </span>
                                </div>
                                <div className="text-xs font-mono text-slate-300 break-all mt-1 bg-slate-950 p-2 rounded border border-slate-800" title={client.kyc_session_id}>
                                    {client.kyc_session_id || 'No Session Created'}
                                </div>
                                <div className="text-xs text-slate-400 mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Workflow:</span>
                                        <span className="font-semibold text-slate-200">Compliance & IDV</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 mt-3 border-t border-slate-800/80 text-[11px] text-slate-500">
                                Synced: {client.kyc_updated_at ? new Date(client.kyc_updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                            </div>
                        </div>
                    </div>

                    {/* Detailed Compliance Checkpoints Grid */}
                    <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-700/50">
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                            <Shield size={14} className="text-blue-400" /> Compliance Verification Checklist
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex items-start gap-2.5">
                                <div className="mt-0.5">
                                    {(client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') ? (
                                        <CheckCircle size={16} className="text-emerald-400" />
                                    ) : ['declined', 'rejected'].includes(String(client.kyc_status).toLowerCase()) ? (
                                        <XCircle size={16} className="text-red-400" />
                                    ) : (
                                        <Clock size={16} className="text-amber-400" />
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white">Government ID Validity</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        MRZ format, security features & document integrity
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex items-start gap-2.5">
                                <div className="mt-0.5">
                                    {(client.kyc_approved || String(client.kyc_status).toLowerCase() === 'approved') ? (
                                        <CheckCircle size={16} className="text-emerald-400" />
                                    ) : (
                                        <Clock size={16} className="text-amber-400" />
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white">Biometric 3D Liveness</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        Anti-spoofing facial match & active liveness verification
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex items-start gap-2.5">
                                <div className="mt-0.5">
                                    {client.aml_hits === 0 ? (
                                        <CheckCircle size={16} className="text-emerald-400" />
                                    ) : client.aml_hits > 0 ? (
                                        <AlertOctagon size={16} className="text-red-400" />
                                    ) : (
                                        <Clock size={16} className="text-slate-400" />
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white">Sanctions & PEP Screening</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        UN, OFAC, EU, UK HMT & global politically exposed persons
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex items-start gap-2.5">
                                <div className="mt-0.5">
                                    <CheckCircle size={16} className="text-indigo-400" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white">Ongoing Watchlist Monitor</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        Real-time continuous webhook updates for new listings
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 1. Internal Document Vault */}
                {(!isSumsubTier || (documents || []).length > 0 || isEnhancedKycRequired) && (
                <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <FileText size={20} className="text-slate-400"/> Internal Document Vault
                            </h3>
                            <p className="text-xs text-green-400 mt-1">Manual KYC (CFD Trading & Admin Uploads)</p>
                        </div>
                        
                        {/* 🚨 THE UPGRADE: Added the Decoupled Master Verification Actions */}
                        <div className="flex gap-3">
                            {/* 🔒 MANUAL KYC DECISION ACTION HIDDEN (Preserved for automated Didit KYC integration)
                            <button 
                                onClick={async () => {
                                    if(!window.confirm("Are you sure you want to verify this account? This will grant trading access.")) return;
                                    
                                    const targetEmail = client.email || baseClient?.email;
                                    const exactNommiaId = client.id; 
                                    
                                    if (!targetEmail) {
                                        return alert("Cannot verify: Client email address is missing from state. Please refresh.");
                                    }
                                    
                                    try {
                                        // ==========================================
                                        // 1. LEGACY NOMMIA API SYNC (Your original code)
                                        // ==========================================
                                        try {
                                            await authFetch(`https://nommia-crm-backend-production.up.railway.app/api/kyc/update`, {
                                                method: 'POST',
                                                body: JSON.stringify({
                                                    id: exactNommiaId, 
                                                    email: targetEmail,
                                                    status: 'approved',
                                                    reject_reason: ''
                                                })
                                            });
                                        } catch (legacyErr) {
                                            console.warn("Legacy Nommia API Sync skipped.");
                                        }

                                        // ==========================================
                                        // 2. NEW SUPABASE & XVALLEY SYNC (Your original code)
                                        // ==========================================
                                        
                                        // Fetch Real UUID
                                        const { data: profRow } = await supabase
                                            .from('profiles')
                                            .select('user_id')
                                            .eq('email', targetEmail)
                                            .maybeSingle();

                                        const realUuid = profRow?.user_id || client.id;

                                        // Update Profiles Table
                                        if (profRow) {
                                            await supabase.from('profiles').update({ kyc_status: 'approved' }).eq('email', targetEmail);
                                        }

                                        // Update User Broker Accounts
                                        const { data: existingRow } = await supabase
                                            .from('user_broker_accounts')
                                            .select('id, user_id')
                                            .eq('email', targetEmail)
                                            .maybeSingle();
                                        
                                        if (existingRow) {
                                            await supabase.from('user_broker_accounts').update({ 
                                                kyc_approved: true,
                                                kyc_status: 'approved' 
                                            }).eq('email', targetEmail);
                                        } else {
                                            await supabase.from('user_broker_accounts').insert({ 
                                                user_id: realUuid, 
                                                email: targetEmail, 
                                                kyc_approved: true,
                                                kyc_status: 'approved',
                                                broker_name: 'xvalley', 
                                                account_id: 'pending_creation',
                                                first_name: client.firstName || client.name?.split(' ')[0],
                                                last_name: client.lastName || client.name?.split(' ').slice(1).join(' '),
                                                name: client.name
                                            });
                                        }

                                        // XValley Sync (Direct)
                                        const xvalleyEmail = targetEmail || (client.id.includes('@') ? client.id : null);
                                        if (xvalleyEmail) {
                                            fetch(`${process.env.REACT_APP_XVALLEY_API_URL || 'https://xvalley-api-production.up.railway.app'}/api/xvalley/kyc-update`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                    email: xvalleyEmail,
                                                    firstName: client.firstName, 
                                                    lastName: client.lastName,   
                                                    phone: client.phone,         
                                                    countryIso: 'GB',            
                                                    kycStatus: 'Verified'
                                                })
                                            }).catch(e => console.warn("XValley Sync Error:", e));
                                        }

                                        // ==========================================
                                        // 3. UI STATE & AUTOMATION
                                        // ==========================================
                                        
                                        setClient(prev => ({ ...prev, status: 'kyc_approved', kycStatus: 'Approved', advancedKycStatus: 'Completed' }));
                                        setJourneyStage('kyc_approved');
                                        setIsTradingRestricted(false); 
                                        
                                        await fireAutomationEvent('KYC_APPROVED', {
                                            email: targetEmail,
                                            firstName: client.firstName || client.name?.split(' ')[0] || 'There',
                                            id: client.id
                                        });

                                        logSystemActivity('Account Verified', 'Agent verified the account after reviewing documents.', 'security');
                                        alert("Account Successfully Verified & XValley Synced!");
                                        
                                    } catch (err) {
                                        console.error(err);
                                        alert("Verification Error: Check console for details.");
                                    }
                                }} 
                                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                            >
                                <CheckCircle size={16} /> Verify Account
                            </button>
                            */}
                            
                            <button onClick={() => setShowUploadModal(true)} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2">
                                <Plus size={16} /> Upload Manual Doc
                            </button>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {documents.length > 0 ? documents.map((d, i) => (
                            <div key={i} onClick={() => handleViewDocument(d)} className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex justify-between items-center cursor-pointer hover:border-blue-500/50 transition-colors group">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-slate-800 rounded-lg text-slate-300 group-hover:text-blue-400 transition-colors"><FileText size={24} /></div>
                                    <div><p className="font-bold text-white text-sm group-hover:text-blue-400">{d.type}</p><div className="flex items-center gap-2 mt-1"><span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">{d.source}</span>{d.aiScore && <span className="text-[10px] text-green-400 flex items-center gap-1"><Zap size={10} /> AI: {d.aiScore}%</span>}</div></div>
                                </div>
                                <div className="text-right flex flex-col items-end gap-1">
                                    <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${d.status === 'Approved' ? 'bg-green-500/10 text-green-400' : d.status === 'Rejected' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>{d.status}</span>
                                    <div className="flex items-center gap-3 mt-1">
                                        <p className="text-[10px] text-slate-500">Exp: {d.expiry}</p>
                                        <button onClick={(e) => handleDeleteDocument(d.id, e)} className="text-slate-500 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-500/10"><Trash2 size={12}/></button>
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="text-slate-500 text-center col-span-2 py-8 border border-dashed border-slate-800 rounded-xl">No manual documents found.</div>
                        )}

                        {/* AUTO-GENERATED PENDING SLOTS IF THRESHOLD BREACHED */}
                        {isEnhancedKycRequired && (
                            <>
                                {/* LEVEL 1 EDD: PROOF OF ADDRESS (POA) @ €2,000 THRESHOLD */}
                                <div className="bg-amber-900/10 p-4 rounded-xl border border-amber-500/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 animate-fade-in">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-amber-500/10 rounded-lg text-amber-500"><AlertOctagon size={24} /></div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-amber-400 text-sm">Level 1 EDD: Proof of Address (POA)</p>
                                                <span className="text-[10px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.5 rounded">€2,000 Limit</span>
                                            </div>
                                            <p className="text-[11px] text-slate-400 mt-0.5">Utility bill, bank statement, or council tax letter (&lt; 90 days). Powered by Didit.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                                        <button 
                                            onClick={() => handleTriggerEddSession('POA', true)} 
                                            disabled={isTriggeringEdd}
                                            className="text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                                            title="Send Didit Proof of Address invite email to client"
                                        >
                                            <Send size={12} /> Email Invite
                                        </button>
                                        <button 
                                            onClick={() => handleTriggerEddSession('POA', false)} 
                                            disabled={isTriggeringEdd}
                                            className="text-xs font-bold bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                                        >
                                            <ExternalLink size={12} /> Launch Didit POA
                                        </button>
                                    </div>
                                </div>

                                {/* LEVEL 2 EDD: SOURCE OF FUNDS (SOF) @ €10,000 THRESHOLD */}
                                {isLevel2Required && (
                                    <div className="bg-rose-900/10 p-4 rounded-xl border border-rose-500/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 animate-fade-in">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 bg-rose-500/10 rounded-lg text-rose-500"><ShieldAlert size={24} /></div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-bold text-rose-400 text-sm">Level 2 EDD: Source of Funds (SOF)</p>
                                                    <span className="text-[10px] bg-rose-500/20 text-rose-300 font-bold px-1.5 py-0.5 rounded">€10,000+ Limit</span>
                                                </div>
                                                <p className="text-[11px] text-slate-400 mt-0.5">Financial wealth questionnaire &amp; supporting banking statements. Powered by Didit.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                                            <button 
                                                onClick={() => handleTriggerEddSession('SOF', true)} 
                                                disabled={isTriggeringEdd}
                                                className="text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                                                title="Send Didit Source of Funds questionnaire email to client"
                                            >
                                                <Send size={12} /> Email Invite
                                            </button>
                                            <button 
                                                onClick={() => handleTriggerEddSession('SOF', false)} 
                                                disabled={isTriggeringEdd}
                                                className="text-xs font-bold bg-rose-600 hover:bg-rose-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                                            >
                                                <ExternalLink size={12} /> Launch Didit SOF
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                )}

                {/* 2. Idenfy Intelligence (Standard Automated Check) */}
                {isSumsubTier && (
                <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700">
                    <div className="flex justify-between items-center mb-6"><div><h3 className="text-lg font-bold text-white flex items-center gap-2"><ScanFace size={20} className="text-amber-500"/> Idenfy Intelligence</h3><p className="text-xs text-amber-500 mt-1">Primary KYC Method for {client.tier} Tier</p></div><span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${client.sumsub?.status === 'Approved' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : client.sumsub?.status === 'Flagged' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-slate-800 text-slate-500'}`}>{client.sumsub?.status || 'NOT STARTED'}</span></div>
                    {client.sumsub ? (<div className="space-y-4 relative"><div className="absolute left-6 top-4 bottom-4 w-0.5 bg-slate-700 -z-10"></div>{client.sumsub.checks.map((check, i) => (<div key={i} className="flex items-center gap-4"><div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-slate-800 ${check.status === 'Passed' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{check.status === 'Passed' ? <CheckCircle size={20} /> : <XCircle size={20} />}</div><div className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex-1 flex justify-between items-center"><div><p className="font-bold text-white text-sm">{check.name}</p><p className="text-xs text-slate-500 mt-0.5">{check.time}</p></div>{check.reason && <span className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded font-bold">{check.reason}</span>}</div></div>))}</div>) : (<div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800 border-dashed"><p className="text-slate-500 mb-4">Client has not initiated Banking/Crypto KYC.</p><button className="bg-amber-500 text-slate-900 px-4 py-2 rounded-lg text-sm font-bold">Send Idenfy Invite</button></div>)}
                </div>
                )}

              {/* 3. ENHANCED KYC (Live Threshold Trigger & Intervention) */}
        <div className={`p-6 rounded-2xl border relative overflow-hidden transition-all duration-500 ${isEnhancedKycRequired ? 'bg-slate-900/80 border-red-500/30 shadow-[0_0_30px_rgba(239,68,68,0.1)]' : 'bg-slate-900 border-slate-800'}`}>
                        
                        {/* Header Section */}
                        <div className="flex justify-between items-start relative z-10 mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <AlertOctagon size={20} className={client.advancedKycStatus === 'Completed' ? "text-green-500" : isEnhancedKycRequired ? "text-red-500 animate-pulse" : "text-blue-500"} /> 
                                Enhanced Due Diligence (EDD)
                            </h3>
                            <p className={`text-sm mt-1 ${isEnhancedKycRequired ? 'text-red-400 font-bold' : 'text-slate-400'}`}>
                                {client.advancedKycStatus === 'Completed' 
                                    ? "Verification Complete. Source of Wealth Verified." 
                                    : isLevel2Required
                                    ? "CRITICAL: €10,000 threshold exceeded. Level 2 EDD (Source of Funds) required."
                                    : isLevel1Required
                                    ? "COMPLIANCE NOTICE: €2,000 threshold reached. Level 1 EDD (Proof of Address) required."
                                    : "AML Tier Progress: Level 1 EDD (POA) triggers at €2,000; Level 2 EDD (SOF) triggers at €10,000."}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-slate-500 uppercase font-bold">Live Real-Money Volume</p>
                            <p className={`text-2xl font-bold font-mono ${liveLifetimeVolume >= 2000 ? 'text-red-400' : 'text-slate-300'}`}>
                                €{liveLifetimeVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        </div>

                        {/* STATE 1: TRIGGER REQUIRED (Live Threshold Met) */}
                        {isEnhancedKycRequired && (
                            <div className="animate-fade-in border-t border-red-500/20 pt-6">
                                <div className="mb-6">
                                <div className="flex justify-between text-xs font-bold mb-2">
                                    <span className="text-red-400 flex items-center gap-1"><ShieldAlert size={12}/> {isLevel2Required ? "LEVEL 2 (€10K) THRESHOLD EXCEEDED" : "LEVEL 1 (€2K) THRESHOLD EXCEEDED"}</span>
                                    <span className="text-slate-500">LIMIT: {isLevel2Required ? "€10,000" : "€2,000"}</span>
                                </div>
                                <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-500 w-full"></div>
                                </div>
                                </div>
                                
                                {/* MANUAL INTERVENTION: AI warns, human acts */}
                                <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-red-500/5 p-4 rounded-xl border border-red-500/20">
                                    <div>
                                        <p className="text-sm font-bold text-white mb-1">Compliance Actions &amp; Restrictions</p>
                                        <p className="text-xs text-slate-400">
                                            {isLevel2Required 
                                                ? "Client has crossed €10,000. Source of Funds questionnaire and wealth documentation must be submitted."
                                                : "Client has crossed €2,000. Proof of Address is required per CySEC Section 62 derogation."}
                                        </p>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
                                        <button 
                                            onClick={() => handleTriggerEddSession(isLevel2Required ? 'SOF' : 'POA', true)}
                                            disabled={isTriggeringEdd}
                                            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors"
                                            title="Send invitation email to client"
                                        >
                                            <Send size={13} /> {isTriggeringEdd ? 'Sending...' : 'Email Invite'}
                                        </button>
                                        <button 
                                            onClick={() => handleTriggerEddSession(isLevel2Required ? 'SOF' : 'POA', false)}
                                            disabled={isTriggeringEdd}
                                            className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                                        >
                                            <ExternalLink size={13} /> Launch Session
                                        </button>
                                        <button 
                                            onClick={() => setIsTradingRestricted(!isTradingRestricted)} 
                                            className={`px-4 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-lg ${
                                                isTradingRestricted 
                                                ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600' 
                                                : 'bg-red-600 hover:bg-red-500 text-white shadow-red-900/20'
                                            }`}
                                        >
                                            {isTradingRestricted ? <><CheckCircle size={14} /> Trading Restricted</> : <><Lock size={14} /> Restrict Trading</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STATE 2: NOT REQUIRED (Under Threshold) */}
                        {!isEnhancedKycRequired && client.advancedKycStatus !== 'Completed' && (
                            <div className="mt-2 mb-2">
                            <div className="flex justify-between text-xs font-bold text-slate-500 mb-2">
                                <span>PROGRESS</span>
                                <span>LIMIT: €2,000</span>
                            </div>
                            <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-blue-500 transition-all duration-1000" 
                                    style={{ width: `${Math.min((liveLifetimeVolume / 2000) * 100, 100)}%` }}
                                ></div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <div className="text-xs bg-slate-800 text-slate-500 px-3 py-1 rounded border border-slate-700 flex items-center gap-2">
                                    <CheckCircle size={12} /> Threshold Not Met
                                </div>
                            </div>
                            </div>
                        )}

                        {/* STATE 3: COMPLETED / RESULTS (Results Dashboard) */}
                        {client.advancedKycStatus === 'Completed' && (
                            <div className="border-t border-slate-800 pt-6 animate-fade-in">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex flex-col items-center justify-center text-center">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold mb-1">Status</span>
                                        <span className="text-green-400 font-bold bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 text-xs">PASSED</span>
                                    </div>
                                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex flex-col items-center justify-center text-center">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold mb-1">Risk Score</span>
                                        <span className="text-white font-bold text-lg">Low</span>
                                    </div>
                                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex flex-col items-center justify-center text-center">
                                        <span className="text-[10px] text-slate-500 uppercase font-bold mb-1">Processed By</span>
                                        <span className="text-purple-400 font-bold text-xs flex items-center gap-1"><Zap size={12} /> Agentic AI</span>
                                    </div>
                                </div>

                                <h4 className="text-xs font-bold text-slate-500 uppercase mb-3 ml-1">Verification Results</h4>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-4 bg-slate-950/50 p-3 rounded-lg border border-slate-800">
                                        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white shadow-lg shadow-green-900/20"><CheckCircle size={16} /></div>
                                        <div className="flex-1">
                                            <p className="font-bold text-white text-sm">Source of Wealth (SoW)</p>
                                            <p className="text-xs text-slate-500">Bank Statements Analysis</p>
                                        </div>
                                        <span className="text-xs font-bold text-green-400">VERIFIED</span>
                                    </div>
                                    <div className="flex items-center gap-4 bg-slate-900/50 p-3 rounded-lg border border-slate-800">
                                        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white shadow-lg shadow-green-900/20"><CheckCircle size={16} /></div>
                                        <div className="flex-1">
                                            <p className="font-bold text-white text-sm">Adverse Media Check</p>
                                            <p className="text-xs text-slate-500">Global Database Scan</p>
                                        </div>
                                        <span className="text-xs font-bold text-green-400">CLEAR</span>
                                    </div>
                                    <div className="flex items-center gap-4 bg-slate-900/50 p-3 rounded-lg border border-slate-800">
                                        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white shadow-lg shadow-green-900/20"><CheckCircle size={16} /></div>
                                        <div className="flex-1">
                                            <p className="font-bold text-white text-sm">Politically Exposed Person (PEP)</p>
                                            <p className="text-xs text-slate-500">Tier 1-3 Scan</p>
                                        </div>
                                        <span className="text-xs font-bold text-green-400">NEGATIVE</span>
                                    </div>
                                </div>
                                
                                <div className="mt-4 text-center">
                                    <button className="text-xs text-slate-500 hover:text-white underline flex items-center justify-center gap-1 w-full">
                                        View Full Idenfy Report <FileText size={10} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
            </div>
        )}

        {/* TAB: TICKETS - LIST & WORKSPACE LOGIC */}
          {activeTab === 'tickets' && (
              <div className="h-[calc(100vh-380px)] min-h-[500px]">
                  
                  {!selectedTicket ? (
                      /* VIEW 1: TICKET LIST */
                      <div className="space-y-4 animate-fade-in">
                          <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-bold text-white">Support Tickets</h3>
                              <button 
                                  onClick={() => setShowNewTicketModal(true)} 
                                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-blue-500 transition-colors"
                              >
                                  <Plus size={16} /> New Ticket
                              </button>
                          </div>
                          
                          <div className="space-y-3 overflow-y-auto h-full pr-2">
                              {tickets.length > 0 ? tickets.map((t) => (
                                  <div 
                                      key={t.id} 
                                      onClick={() => handleOpenTicket(t)}
                                      className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 hover:bg-slate-800 hover:border-blue-500/50 cursor-pointer transition-all group"
                                  >
                                      <div className="flex justify-between items-start mb-2">
                                          <div className="flex items-center gap-3">
                                              <span className={`w-2 h-2 rounded-full ${t.status === 'New' ? 'bg-blue-500 animate-pulse' : t.status === 'Open' ? 'bg-amber-500' : 'bg-green-500'}`}></span>
                                              <h4 className="font-bold text-white text-sm group-hover:text-blue-400 transition-colors">{t.subject}</h4>
                                          </div>
                                          <span className="text-xs font-mono text-slate-500">{t.id}</span>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs text-slate-400">
                                          <span className={`px-2 py-0.5 rounded font-bold uppercase ${t.status === 'New' ? 'bg-blue-500/10 text-blue-400' : t.status === 'Open' ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
                                              {t.status}
                                          </span>
                                          <span className="flex items-center gap-1"><Clock size={12}/> {t.created}</span>
                                          <span className={`font-bold ${t.priority === 'High' ? 'text-red-400' : 'text-slate-400'}`}>{t.priority} Priority</span>
                                      </div>
                                  </div>
                              )) : (
                                  <div className="p-12 text-center border border-dashed border-slate-800 rounded-xl">
                                      <p className="text-slate-500 italic">No tickets found for this client.</p>
                                  </div>
                              )}
                          </div>
                      </div>
                  ) : (
                      /* VIEW 2: EXPANDED WORKSPACE */
                      <div className="-m-6 h-[calc(100vh-280px)] border-t border-slate-800 bg-slate-950">
                          <TicketWorkspace 
                            ticket={selectedTicket} 
                            onBack={() => setSelectedTicket(null)} 
                            isEmbedded={true}
                            onActivity={handleTicketActivity}
                            currentUserRole={currentUser?.name || userRole || 'Manager'}
                            currentClient={client} 
                          />
                      </div>
                  )}
              </div>
          )}

        {/* === DISPUTES TAB (Client-Specific) === */}
        {activeTab === 'disputes' && (
            <div className="space-y-6 animate-fade-in">
                <div className="flex justify-between items-center bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <ShieldAlert className="text-amber-500" /> Flagged Transactions
                        </h3>
                        <p className="text-slate-400 text-sm mt-1">Disputes and fraud reports initiated by this client.</p>
                    </div>
                    <div className="bg-slate-800 px-4 py-2 rounded-lg border border-slate-700 text-center">
                        <span className="text-xs text-slate-400 uppercase font-bold block mb-0.5">Total Flags</span>
                        <span className={`text-lg font-bold ${clientDisputes.length > 0 ? 'text-amber-500' : 'text-white'}`}>
                            {clientDisputes.length}
                        </span>
                    </div>
                </div>

                <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase border-b border-slate-800">
                            <tr>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Transaction ID</th>
                                <th className="px-6 py-4">Type</th>
                                <th className="px-6 py-4 text-right">Amount</th>
                                <th className="px-6 py-4 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800 text-sm">
                            {isDisputesLoading ? (
                                <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-500">Loading records...</td></tr>
                            ) : clientDisputes.length > 0 ? (
                                clientDisputes.map(dispute => (
                                    <tr key={dispute.id} className="hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4 text-slate-400 text-xs">{new Date(dispute.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-4 font-mono text-xs text-blue-400">{dispute.transaction_id.substring(0, 16)}...</td>
                                        <td className="px-6 py-4 font-bold text-white uppercase">{dispute.transaction_type}</td>
                                        <td className="px-6 py-4 text-right font-mono font-bold text-amber-500">{dispute.amount}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border whitespace-nowrap ${
                                                dispute.status === 'resolved' ? 'bg-green-500/10 text-green-400 border-green-500/30' : 
                                                dispute.status === 'rejected' ? 'bg-red-500/10 text-red-400 border-red-500/30' : 
                                                dispute.status === 'investigating' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' : 
                                                'bg-amber-500/10 text-amber-500 border-amber-500/30'
                                            }`}>
                                                {dispute.status.replace('_', ' ')}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500 italic">
                                        This client has no flagged transactions.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

{/* === SECURITY TAB (Account Security & InfoSec) === */}
{activeTab === 'security' && (() => {
    // 1. INFOSEC RISK CALCULATOR
    // We generate a smart fallback to demonstrate the InfoSec scoring based on live auth logs.
    const activeSecurityLogs = securityEvents.length > 0 ? securityEvents : [
        { id: 1, type: 'Failed Login Attempt', details: 'Invalid password entered 3 times', location: 'London, UK', ip: '82.11.4.102', time: '10m ago', risk: 'Medium' },
        { id: 2, type: 'Impossible Travel Anomaly', details: 'Login from new country 1h after UK login', location: 'Rome, IT', ip: '151.100.22.4', time: '12m ago', risk: 'High' },
        { id: 3, type: 'Email Address Changed', details: 'Client updated primary email', location: 'London, UK', ip: '82.11.4.102', time: '2 days ago', risk: 'Low' }
    ];

    let calculatedRisk = 5; // Base secure score
    activeSecurityLogs.forEach(log => {
        const type = log.type.toLowerCase();
        if (type.includes('anomaly') || type.includes('travel')) calculatedRisk += 40; // Severe InfoSec trigger
        if (type.includes('failed login')) calculatedRisk += 20; // Brute force warning
        if (type.includes('change') || type.includes('reset')) calculatedRisk += 10; // Sensitive account change
    });
    
    // Cap at 100
    const currentSecurityScore = Math.min(calculatedRisk, 100);

    return (
    <div className="space-y-6 animate-fade-in">
        
        {/* 1. RISK SCORE & CREDENTIALS HEADER */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Risk Score Gauge */}
            <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 flex items-center justify-between relative overflow-hidden">
                <div className="relative z-10">
                    <p className="text-slate-400 text-xs font-bold uppercase mb-1">Account Security Score</p>
                    <div className="flex items-baseline gap-2">
                        <span className={`text-4xl font-bold ${currentSecurityScore >= 50 ? 'text-red-500' : currentSecurityScore >= 20 ? 'text-amber-500' : 'text-emerald-400'}`}>
                            {currentSecurityScore}
                        </span>
                        <span className="text-slate-500 text-sm">/ 100</span>
                    </div>
                    <p className={`text-xs mt-2 font-bold px-2 py-1 rounded w-fit ${currentSecurityScore >= 50 ? 'bg-red-500/20 text-red-400' : currentSecurityScore >= 20 ? 'bg-amber-500/20 text-amber-500' : 'bg-emerald-500/20 text-emerald-400'}`}>
                        {currentSecurityScore >= 50 ? 'CRITICAL (Compromise Likely)' : currentSecurityScore >= 20 ? 'ELEVATED RISK' : 'SECURE'}
                    </p>
                </div>
                <Shield size={48} className={`opacity-20 ${currentSecurityScore >= 50 ? 'text-red-500' : currentSecurityScore >= 20 ? 'text-amber-500' : 'text-emerald-500'}`} />
            </div>

            {/* Quick Actions (Password Reset) */}
            <div className="md:col-span-2 bg-slate-800/50 p-6 rounded-2xl border border-slate-700 flex flex-col justify-center">
                    <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="font-bold text-white flex items-center gap-2"><Key size={20} className="text-blue-500"/> Credentials & Access</h3>
                        <p className="text-sm text-slate-400">Manage login methods and recovery.</p>
                    </div>
                    <button 
                        disabled={currentSecurityScore > 80} 
                        className={`px-4 py-2 rounded-lg font-bold flex items-center gap-2 text-sm transition-all ${currentSecurityScore > 80 ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                        onClick={async () => {
                            const clientPhone = client.phone;
                            if (!clientPhone || clientPhone === 'N/A' || clientPhone === 'Unknown') {
                                return alert("Cannot send: No valid phone number on file.");
                            }

                            try {
                                // 1. Generate a 6-digit recovery code
                                const recoveryCode = Math.floor(100000 + Math.random() * 900000).toString();
                                
                                // 2. Fire the specific 'password_recovery' WhatsApp template!
                                await fetch(`${process.env.REACT_APP_NOMMIA_API_URL}/api/wa/send-otp`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ 
                                        phone: clientPhone, 
                                        code: recoveryCode, 
                                        template: 'password_recovery' 
                                    })
                                });

                                alert(`Success! WhatsApp password recovery code dispatched to ${clientPhone}.`);
                                handleSecurityAction('Admin Password Reset WhatsApp Sent', -10); 
                            } catch (err) {
                                console.error("WhatsApp Delivery Failed:", err);
                                alert("Failed to dispatch WhatsApp recovery code. Check Railway logs.");
                            }
                        }}
                    >
                        <Send size={14} /> Send Password Reset
                    </button>
                    
                    <button 
                        onClick={handleImpersonate}
                        disabled={isImpersonating}
                        className="px-4 py-2 rounded-lg font-bold flex items-center gap-2 text-sm bg-purple-600 hover:bg-purple-500 text-white transition-all disabled:opacity-50 ml-2"
                    >
                        <LogOut size={14} className="transform rotate-180" /> {isImpersonating ? 'Generating...' : 'Login as Client'}
                    </button>

                    </div>
                    <div className="flex gap-4 text-sm text-slate-400">
                    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> 2FA Enabled</div>
                    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> Email Verified</div>
                    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-slate-500"></div> No Active Sessions (VPN)</div>
                    </div>
            </div>
        </div>

        {/* 2. SECURITY EVENTS LOG (Now Purely InfoSec) */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-950/30">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                    <Lock size={16} className="text-blue-400" /> Access & Authorization Log
                </h3>
                <span className="text-xs text-slate-500">Live InfoSec Feed</span>
            </div>
            <table className="w-full text-left text-sm">
                <thead className="bg-slate-950/50 text-xs font-bold text-slate-500 uppercase">
                    <tr>
                        <th className="px-6 py-3">Event Type</th>
                        <th className="px-6 py-3">Details</th>
                        <th className="px-6 py-3">Location / IP</th>
                        <th className="px-6 py-3 text-right">Time</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                    {activeSecurityLogs.map((event) => (
                        <tr key={event.id} className="hover:bg-slate-800/30 transition-colors animate-fade-in">
                            <td className="px-6 py-4 font-bold text-white flex items-center gap-2">
                                {event.risk === 'Critical' || event.risk === 'High' ? <ShieldAlert size={14} className="text-red-500" /> : <Shield size={14} className="text-slate-500" />}
                                {event.type}
                            </td>
                            <td className="px-6 py-4 text-slate-300">{event.details}</td>
                            <td className="px-6 py-4 text-xs text-slate-500">
                                <div className="flex items-center gap-1 text-slate-400"><Globe size={10} /> {event.location}</div>
                                <div className="font-mono mt-0.5">{event.ip}</div>
                            </td>
                            <td className="px-6 py-4 text-right text-slate-500 text-xs">{event.time}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

{/* 3. BIO-RECOVERY PROTOCOL (Third-Party WebSDK Integration) */}
        <div className="bg-red-900/10 border border-red-500/30 p-6 rounded-2xl">
            <div className="flex items-center gap-4 mb-4">
                <ScanFace size={32} className="text-red-500" />
                <h3 className="text-xl font-bold text-white">Bio-Recovery Protocol</h3>
            </div>
            
            {bioRecoveryStage === 'idle' && (
                <div className="animate-fade-in">
                    <p className="text-red-300/80 text-sm mb-6">Generates a secure, one-time Idenfy/Sumsub link. The client must complete a live 3D facial scan and ID match on their smartphone to prove identity before you can reset their credentials.</p>
                        <button 
                        onClick={async () => {
                            setBioRecoveryStage('generating');
                            handleSecurityAction('Bio-Recovery Link Generated', 0);
                            
                            const clientPhone = client.phone;
                            const recoveryLink = `https://kyc.nommia.io/recover/${client.id}`; // The secure WebSDK URL
                            
                            try {
                                // Send the direct message via our WhatsApp proxy
                                if (clientPhone && clientPhone !== 'N/A' && clientPhone !== 'Unknown') {
                                    await fetch(`${process.env.REACT_APP_NOMMIA_API_URL}/api/wa/send-message`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ 
                                            phone: clientPhone, 
                                            message: `NOMMIA SECURITY ALERT:\n\nPlease complete your 3D facial verification to secure your account. Click your secure link here: ${recoveryLink}` 
                                        })
                                    });
                                }
                                
                                setBioRecoveryStage('awaiting_client');
                                handleSecurityAction('Bio-Recovery WhatsApp Sent', 0);
                            } catch (err) {
                                console.error("WhatsApp Link Delivery Failed:", err);
                                alert("Failed to dispatch WhatsApp liveness link.");
                                setBioRecoveryStage('idle');
                            }
                        }} 
                        className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-xl font-bold w-full flex items-center justify-center gap-2 shadow-lg shadow-red-900/20 transition-all"
                    >
                        <Smartphone size={18} /> Generate & Send 3D Liveness Link
                    </button>
                </div>
            )}
            
            {bioRecoveryStage === 'generating' && (
                <div className="text-center py-8 animate-fade-in">
                    <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-red-400 font-mono text-sm">Requesting secure session token from KYC Provider...</p>
                </div>
            )}

            {bioRecoveryStage === 'awaiting_client' && (
                <div className="bg-slate-900/80 border border-slate-700 p-8 rounded-xl text-center animate-fade-in">
                    <Smartphone size={40} className="text-amber-500 mx-auto mb-4 animate-pulse" />
                    <h4 className="font-bold text-white text-lg mb-2">Awaiting Client Action</h4>
                    <p className="text-sm text-slate-400 mb-6">Secure WebSDK link sent via SMS to <span className="text-white font-mono">{client.phone}</span>. Waiting for client to complete facial scan...</p>
                    
                    {/* DEV TOOL: This simulates the webhook arriving from Idenfy */}
                    <div className="pt-4 border-t border-slate-800">
                        <button 
                            onClick={() => {
                                setBioRecoveryStage('approved');
                                handleSecurityAction('Bio-Recovery Verified (Face Match)', -50); 
                            }}
                            className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white px-4 py-2 rounded border border-slate-700 transition-colors"
                        >
                            [Dev] Simulate Successful Webhook
                        </button>
                    </div>
                </div>
            )}
            
            {bioRecoveryStage === 'approved' && (
                <div className="animate-fade-in">
                    <div className="bg-green-500/10 p-6 rounded-xl border border-green-500/30 mb-6">
                        <div className="flex items-center gap-3 mb-2"><CheckCircle className="text-green-500" size={24} /><h4 className="text-lg font-bold text-white">Identity Verified</h4></div>
                        <p className="text-green-400/80 text-sm mb-4">3D Biometric scan successfully matched original KYC document profile.</p>
                        <div className="bg-slate-900/50 p-3 rounded-lg border border-green-500/20 flex justify-between items-center">
                            <span className="text-xs text-slate-400 uppercase font-bold">Session ID</span>
                            <span className="font-mono text-green-400 font-bold">{client.sumsubCertificate || 'IDENFY-REQ-99281'}</span>
                        </div>
                    </div>
                    
                    {/* These dangerous actions are only unlocked AFTER the face match */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button onClick={() => {
                            handleSecurityAction('Admin Force Password Reset', 0);
                            alert('Password reset forced.');
                        }} className="bg-slate-800 hover:bg-slate-700 text-white p-4 rounded-xl border border-slate-700 flex flex-col items-center gap-2 transition-all group">
                            <Key size={24} className="text-amber-500 group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-sm">Force Password Reset</span>
                        </button>
                        <button onClick={() => setShowEmailUpdateModal(true)} className="bg-slate-800 hover:bg-slate-700 text-white p-4 rounded-xl border border-slate-700 flex flex-col items-center gap-2 transition-all group">
                            <RefreshCw size={24} className="text-blue-500 group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-sm">Update Email Address</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    </div>
    );
})()}

      {/* EMAIL UPDATE MODAL */}
      {showEmailUpdateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
                <button onClick={() => setShowEmailUpdateModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
                    <X size={24} />
                </button>
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center border border-blue-500/30">
                        <Mail size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">Update Primary Email</h2>
                        <p className="text-xs text-slate-400">Change Supabase Auth & XValley Ledger</p>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">New Email Address</label>
                        <input 
                            type="email" 
                            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                            placeholder="e.g., valliidus1987@gmail.com"
                            value={newEmailInput}
                            onChange={(e) => setNewEmailInput(e.target.value)}
                        />
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3 text-amber-200/80">
                        <AlertOctagon size={20} className="shrink-0 text-amber-500" />
                        <div className="text-xs">
                            <strong className="text-amber-500 block mb-1">Warning: Global Update</strong>
                            This action cannot be cleanly reversed. It instantly overwrites the user's main trading account login and cascades through all SQL metrics and ledgers.
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-slate-800">
                        <button 
                            onClick={() => setShowEmailUpdateModal(false)}
                            className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleEmailUpdateSubmit}
                            disabled={isUpdatingEmail}
                            className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isUpdatingEmail ? (
                                <>
                                    <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Confirm Change
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}
  {/* === COMMS & AUDIT TAB (With "Closed" Badge Logic) === */}
          {activeTab === 'comms' && (
              <div className="flex flex-col h-full gap-6 animate-fade-in">
                 
                 {/* 1. COMMUNICATION INBOX */}
                 <div className="flex-1 bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden min-h-[300px] flex">
                     {/* Sidebar List */}
                     <div className="w-1/3 border-r border-slate-800 flex flex-col">
                         <div className="p-4 border-b border-slate-800 bg-slate-950/30 flex justify-between items-center">
                             <h3 className="font-bold text-white text-sm">Inbox</h3>
                             <button onClick={() => setShowNewCommModal(true)} className="bg-blue-600 p-1.5 rounded text-white hover:bg-blue-500"><Plus size={14} /></button>
                         </div>
                         <div className="flex-1 overflow-y-auto">
                             {communications && communications.length > 0 ? communications.map((comm) => (
                                 <div 
                                    key={comm.id} 
                                    onClick={() => setSelectedCommId(comm.id)} 
                                    className={`p-4 border-b border-slate-800 cursor-pointer transition-colors hover:bg-slate-800/50 ${selectedCommId === comm.id ? 'bg-slate-800 border-l-2 border-l-amber-500' : ''}`}
                                 >
                                     <div className="flex justify-between mb-1">
                                         <div className="flex items-center gap-2">
                                             {/* Dynamic Icons & Colors based on Status */}
                                             {comm.type === 'ticket' ? <ShieldAlert size={12} className={comm.status === 'Closed' ? "text-slate-500" : "text-purple-400"}/> : 
                                              comm.type === 'whatsapp' ? <MessageCircle size={12} className="text-green-400"/> : 
                                              <Mail size={12} className="text-blue-500"/>}
                                             
                                             <span className={`text-xs font-bold uppercase ${
                                                 comm.status === 'Closed' ? 'text-slate-500 line-through' : 
                                                 comm.type === 'ticket' ? 'text-purple-400' : 
                                                 comm.type === 'whatsapp' ? 'text-green-400' : 'text-blue-500'
                                             }`}>
                                                 {comm.type}
                                             </span>
                                         </div>

                                             {/* BADGE CHECK */}
                                             {comm.status === 'Closed' ? (
                                             <span className="text-[10px] font-bold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded border border-red-500/20">CLOSED</span>
                                         ) : (
                                             <span className="text-[10px] text-slate-500">{comm.date}</span>
                                         )}
                                     </div>

                                     <h4 className={`text-sm font-bold truncate ${comm.status === 'Closed' ? 'text-slate-500' : 'text-white'}`}>
                                         {comm.subject}
                                     </h4>
                                     <p className="text-xs text-slate-500 truncate">
                                          {comm.messages && comm.messages.length > 0 
                                          ? stripHtml(comm.messages[comm.messages.length - 1].text)
                                          : 'No preview available'}
                                      </p>
                                 </div>
                             )) : (
                                 <div className="p-8 text-center text-slate-500 italic text-sm">No communication history.</div>
                             )}
                         </div>
                     </div>
                     
                     {/* Message View Area */}
                     <div className="flex-1 bg-slate-950/30 flex flex-col">
                         {selectedComm ? (
                            <>
                                <div className="p-4 border-b border-slate-800 bg-slate-900 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-bold text-white text-lg">{selectedComm.subject}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-slate-400 font-mono">{selectedComm.id}</span>
                                            <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-400 uppercase">{selectedComm.status}</span>
                                        </div>
                                    </div>
                                    
                                    {/* The Resolve Button Injection */}
                                    {selectedComm.status !== 'Closed' && (
                                        <button 
                                            onClick={() => handleEndChat(selectedComm.id)}
                                            className="flex items-center gap-2 px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                                        >
                                            <XCircle size={16} />
                                            Resolve Ticket
                                        </button>
                                    )}
                                </div>
                                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                    {selectedComm.messages.map((msg, i) => (
                                        <div key={i} className={`flex ${msg.sender === 'Client' ? 'justify-start' : 'justify-end'}`}>
                                            
                                            {/* 🚨 THE FIX 1: Widen the bubble to 95% if it's an email so templates have room to render */}
                                            <div className={`rounded-2xl p-4 ${selectedComm.type === 'email' ? 'max-w-[95%] w-full' : 'max-w-[70%]'} ${
                                                msg.sender === 'Client' ? 'bg-slate-800 text-slate-200 rounded-tl-none' : 
                                                selectedComm.type === 'whatsapp' ? 'bg-green-900/40 text-green-100 border border-green-500/20 rounded-tr-none' : 
                                                'bg-blue-900/40 text-blue-100 border border-blue-500/20 rounded-tr-none'
                                            }`}>
                                                <div className="flex justify-between items-center mb-2 gap-4">
                                                    <span className="text-xs font-bold uppercase opacity-75">{msg.sender}</span>
                                                    <span className="text-[10px] opacity-50">{msg.time}</span>
                                                </div>
                                                
                                                {/* 🚨 THE FIX 2: Smart HTML Rendering Engine */}
                                                {selectedComm.type === 'email' && (msg.text.includes('<html') || msg.text.includes('<body') || msg.text.includes('<table')) ? (
                                                    // Full HTML Template -> Render isolated inside an iframe
                                                    <div className="bg-white rounded-xl overflow-hidden mt-3 border border-slate-300">
                                                        <iframe 
                                                            srcDoc={msg.text} 
                                                            className="w-full border-0 min-h-[450px] bg-white" 
                                                            title="email-content"
                                                        />
                                                    </div>
                                                ) : selectedComm.type === 'email' && msg.text.includes('<') ? (
                                                    // Minor HTML (just <br> or <b> tags) -> Render directly
                                                    <div 
                                                        className="text-sm leading-relaxed" 
                                                        dangerouslySetInnerHTML={{ __html: msg.text }} 
                                                    />
                                                ) : (
                                                    // WhatsApp or Tickets -> Strip HTML and show safe text
                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{stripHtml(msg.text)}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                         ) : (
                             <div className="flex-1 flex flex-col justify-center items-center text-slate-500">
                                 <MessageSquare size={48} className="opacity-20 mb-4"/>
                                 <p className="text-sm">Select a conversation to view details</p>
                             </div>
                         )}
                     </div>
                 </div>

{/* 2. SYSTEM AUDIT LOG */}
                 <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden flex-1">
                      <div className="p-4 border-b border-slate-800 bg-slate-950/30 flex items-center gap-2">
                          <Server size={16} className="text-purple-400" />
                          <h3 className="font-bold text-white text-sm uppercase tracking-wide">System Audit & Activity Log</h3>
                      </div>
                      
                      <div className="overflow-x-auto">
                          <table className="w-full text-left">
                              <thead className="bg-slate-950/50 text-xs font-bold text-slate-500 uppercase border-b border-slate-800">
                                  <tr>
                                      <th className="px-6 py-3">Event Type</th>
                                      <th className="px-6 py-3">Description</th>
                                      <th className="px-6 py-3">Actor (Who)</th>
                                      <th className="px-6 py-3 text-right">Time</th>
                                  </tr>
                              </thead>
                             <tbody className="divide-y divide-slate-800 text-sm">
                                  {systemNotifications && systemNotifications.length > 0 ? (
                                      systemNotifications.map((log, i) => (
                                          <tr key={log.id || i} className="hover:bg-slate-800/30 transition-colors animate-fade-in">
                                          <td className="px-6 py-3 font-medium text-white flex items-center gap-2">
                                              {log.type === 'edit' ? <Edit2 size={14} className="text-amber-400"/> :
                                               log.type === 'key' ? <Key size={14} className="text-blue-400"/> :
                                               log.type === 'file' ? <FileText size={14} className="text-green-400"/> :
                                               log.type === 'ticket' ? <MessageSquare size={14} className="text-purple-400"/> :
                                               log.type === 'security' ? <Shield size={14} className="text-red-400"/> :
                                               <Server size={14} className="text-slate-400"/>}
                                              {log.action}
                                          </td>
                                          <td className="px-6 py-3 text-slate-300">{log.desc}</td>
                                          <td className="px-6 py-3">
                                              <span className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase ${
                                                  log.actor?.toLowerCase() === 'client' || log.actor === 'System' 
                                                  ? 'bg-slate-700 text-slate-300 border-slate-600' 
                                                  : 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                                              }`}>
                                                  {log.actor}
                                              </span>
                                          </td>
                                          <td className="px-6 py-3 text-right text-slate-500 font-mono text-xs">{log.time}</td>
                                      </tr>
                                      ))
                                  ) : (
                                      <tr>
                                          <td colSpan="4" className="px-6 py-12 text-center text-slate-500 italic">
                                              No system activity recorded yet.
                                          </td>
                                      </tr>
                                  )}
                              </tbody>
                          </table>
                      </div>
                  </div>
              </div>
          )}

          {/* TAB: NETWORK (Sub-Affiliates) */}
          {activeTab === 'network' && (
              <div className="space-y-6 animate-fade-in">
                 <div className="flex justify-between items-center">
                    <div>
                       <h3 className="text-xl font-bold text-white">Partner Network</h3>
                       <p className="text-slate-400 text-sm">Sub-Affiliates and Referred Clients</p>
                    </div>
                    <div className="bg-slate-800 px-4 py-2 rounded-lg border border-slate-700">
                       <span className="text-xs text-slate-400 uppercase font-bold mr-2">Total Network</span>
                       <span className="text-white font-bold">{client.subAffiliates?.length || 0}</span>
                    </div>
                 </div>

                 <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                    <table className="w-full text-left">
                       <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase border-b border-slate-800">
                          <tr>
                             <th className="px-6 py-4">Name</th>
                             <th className="px-6 py-4">Role</th>
                             <th className="px-6 py-4">Joined</th>
                             <th className="px-6 py-4 text-right">Revenue</th>
                             <th className="px-6 py-4 text-right">Action</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-800 text-sm">
                          {client.subAffiliates && client.subAffiliates.length > 0 ? (
                             client.subAffiliates.map((sub, i) => (
                                <tr key={i} className="hover:bg-slate-800/50 transition-colors">
                                   <td className="px-6 py-4 font-medium text-white">{sub.name}</td>
                                   <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${sub.role === 'IB' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>{sub.role}</span></td>
                                   <td className="px-6 py-4 text-slate-400">{sub.joinedDate}</td>
                                   <td className="px-6 py-4 text-right text-green-400 font-mono font-bold">${sub.revenue?.toLocaleString() || '0'}</td>
                                   <td className="px-6 py-4 text-right">
                                     <button 
                                        onClick={() => onClientSwitch && onClientSwitch({
                                            ...sub,
                                            documents: sub.documents || [],
                                            tickets: sub.tickets || [],
                                            communications: sub.communications || [],
                                            tradingAccounts: sub.tradingAccounts || [],
                                        })} 
                                        className="text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-all"
                                      >
                                        View Profile
                                      </button>
                                   </td>
                                </tr>
                             ))
                          ) : (
                             <tr><td colSpan="5" className="px-6 py-12 text-center text-slate-500 italic">No sub-affiliates found.</td></tr>
                          )}
                       </tbody>
                    </table>
                 </div>
              </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ClientProfile;







